// Template catalog — mirrored from legacy-mockup/data.jsx with proper TS
// types. These IDs are also stored in invitations.template_id on the
// backend; renaming an id here would break existing invitations, so
// don't rename — only add or deprecate.

import { isIntlSite } from "./market";

export type TemplateCategory =
  | "wedding"
  | "nikoh"
  | "birthday"
  | "beshik"
  | "corporate"
  | "memorial"
  | "yubiley"
  | "fotixa"
  | "sunnat"
  | "graduation"
  | "uy"
  | "sevgi"
  | "iftor"
  | "bookie";

export type TemplateLayout =
  | "photo-bleed"
  | "noir-gold"
  | "watercolor"
  | "arch-photo"
  | "modernist"
  | "playful"
  | "soft-pastel"
  | "corporate-grid"
  | "memorial-frame"
  | "art-deco"
  | "monogram-frame"
  | "border-pattern"
  | "split-text"
  // 10 new layouts (round 2)
  | "miniature"
  | "coastal"
  | "milestone"
  | "rose-gold"
  | "suzani-burst"
  | "scroll-academic"
  | "champagne"
  | "neon-tech"
  | "geometric-mosaic"
  | "hospitality"
  // 10 new layouts (round 3)
  | "corp-anniversary"
  | "retail-opening"
  | "conf-tracks"
  | "masterclass"
  | "charity-elegant"
  | "housewarming"
  | "sunrise"
  | "milestone-birthday"
  | "lovers"
  | "iftar"
  // 5 new layouts (round 4 — wedding maximalist)
  | "baroque-gold"
  | "tulip-miniature"
  | "celestial-foil"
  | "garden-cascade"
  | "bukhara-royal"
  // 5 new layouts (round 5 — birthday maximalist)
  | "kid-carnival"
  | "princess-castle"
  | "sparkler-milestone"
  | "vintage-fair"
  | "disco-neon"
  // User-imported standalone HTML templates — preserved exactly as the
  // designer authored them; layout is just a small preview thumbnail and
  // the variant renders the original HTML inside a full-bleed iframe.
  | "diyora-preview"
  | "avgust-preview"
  | "sentabr-preview"
  // Round 6 — 10 mobile-first iframe-static wedding templates
  | "mehrli-preview"
  | "tilakli-preview"
  | "olovgul-preview"
  | "bezakli-preview"
  | "ipakli-preview"
  | "sokin-preview"
  | "mavjli-preview"
  | "lavanda-preview"
  | "olcha-preview"
  | "quyoshli-preview"
  | "beshik-toyi-preview"
  // Round 7 — 5 mixed iframe-static templates
  | "yubiley-korporativ-preview"
  | "uchrashuv-preview"
  | "barakali-preview"
  | "alisher-muazzam-preview"
  | "anvar-masuma-preview"
  // Round 8 — 4 designer iframe-static templates
  | "birthday-riso-preview"
  | "corporate-quarterly-preview"
  | "marry-me-one-question-preview"
  | "wedding-editorial-cinema-preview"
  // Round 9 — 25 bookie-style iframe-static templates (premium category)
  | "bookie-paper-gold-preview"
  | "bookie-sage-preview"
  | "bookie-wine-preview"
  | "bookie-teal-preview"
  | "bookie-midnight-preview"
  | "bookie-copper-preview"
  | "bookie-mauve-preview"
  | "bookie-emerald-preview"
  | "bookie-blush-preview"
  | "bookie-navy-preview"
  | "bookie-amber-preview"
  | "bookie-pearl-preview"
  | "bookie-terracotta-preview"
  | "bookie-charcoal-preview"
  | "bookie-rose-preview"
  | "bookie-olive-preview"
  | "bookie-burgundy-preview"
  | "bookie-jade-preview"
  | "bookie-saffron-preview"
  | "bookie-onyx-preview"
  | "bookie-champagne-preview"
  | "bookie-forest-preview"
  | "bookie-coral-preview"
  | "bookie-plum-preview"
  | "bookie-azure-preview"
  // Round 10 — 2 new designer iframe-static templates
  | "nikoh-preview"
  | "wedding-is-coming-preview"
  // Round 11 — Buklama paper-diorama / tunnel-book template
  | "buklama-preview"
  // Round 12 — 2 designer watercolor wedding templates
  | "garden-arch-preview"
  | "mountain-preview"
  // Round 13 — celestial night-sky wedding template
  | "starry-night-preview"
  // Round 14 — terracotta dried-floral wedding template
  | "terracotta-preview"
  // Round 15 — paper-cut tunnel-book fairy-tale wedding template
  | "ertak-preview"
  // Round 16 — twilight fairy-tale tunnel-book wedding template
  | "pari-preview"
  // Round 17 — kids' circus / carnival birthday template
  | "sharlar-preview"
  // Round 18 — sweet kids garden-party birthday template
  | "asalari-preview"
  // Round 19 — celestial-library storybook birthday keepsake
  | "rumaysa-preview"
  // Round 20 — autumn-marigold watercolor wedding invitation
  | "marigold-preview"
  // Round 21 — peacock-motif emerald nikoh invitation
  | "tovus-preview"
  // Round 22 — midnight-blue & gold "Tungi Saroy" wedding invitation
  | "midnight-palace-preview"
  // Round 23 — "Yomg'irdan so'ng" night-to-day "rain clears into sunshine" wedding invitation
  | "yomgir-preview"
  // Round 24 — "Yaylov" sunny-meadow wedding invitation
  | "yaylov-preview"
  // Round 25 — "Sakura" sakura-falls wedding invitation
  | "sakura-preview"
  // Round 26 — "Qog'oz ertagi" book-open storybook wedding invitation
  | "qogoz-preview"
  // Round 27 — "Oltin yo'l" railway-journey wedding invitation
  | "oltin-yol-preview"
  // Round 28 — "Cheksiz osmon" endless-sky wedding invitation
  | "osmon-preview"
  // Round 29 — "To'rt fasl" four-seasons-scroll wedding invitation
  | "fasllar-preview"
  // Round 30 — "Envelope" wax-seal envelope-reveal wedding invitation
  | "envelope-preview";

export type TemplateVariant =
  | "editorial"
  | "magazine"
  | "photoled"
  | "ceremonial"
  | "minimal"
  | "celebration"
  | "newborn"
  | "agenda"
  | "memorial"
  // 10 new variants (round 2)
  | "botanical"
  | "seaside"
  | "jubilee"
  | "engagement"
  | "celebration-sunnat"
  | "academia"
  | "adult-birthday"
  | "launch-event"
  | "reverent"
  | "restaurant-opening"
  // 10 new variants (round 3)
  | "corp-anniversary"
  | "retail-opening"
  | "conf-tracks"
  | "masterclass"
  | "charity-elegant"
  | "housewarming"
  | "sunrise-wedding"
  | "milestone-birthday"
  | "lovers"
  | "iftar"
  // 5 new variants (round 4 — wedding maximalist)
  | "baroque-gold"
  | "tulip-miniature"
  | "celestial-foil"
  | "garden-cascade"
  | "bukhara-royal"
  // 5 new variants (round 5 — birthday maximalist)
  | "kid-carnival"
  | "princess-castle"
  | "sparkler-milestone"
  | "vintage-fair"
  | "disco-neon"
  // User-imported standalone HTML templates — see TemplateLayout above.
  | "diyora-static"
  | "avgust-static"
  | "sentabr-static"
  // Round 6 — 10 mobile-first iframe-static wedding templates
  | "mehrli-static"
  | "tilakli-static"
  | "olovgul-static"
  | "bezakli-static"
  | "ipakli-static"
  | "sokin-static"
  | "mavjli-static"
  | "lavanda-static"
  | "olcha-static"
  | "quyoshli-static"
  | "beshik-toyi-static"
  // Round 7 — 5 mixed iframe-static templates
  | "yubiley-korporativ-static"
  | "uchrashuv-static"
  | "barakali-static"
  | "alisher-muazzam-static"
  | "anvar-masuma-static"
  // Round 8 — 4 designer iframe-static templates
  | "birthday-riso-static"
  | "corporate-quarterly-static"
  | "marry-me-one-question-static"
  | "wedding-editorial-cinema-static"
  // Round 9 — 25 bookie-style iframe-static templates
  | "bookie-paper-gold-static"
  | "bookie-sage-static"
  | "bookie-wine-static"
  | "bookie-teal-static"
  | "bookie-midnight-static"
  | "bookie-copper-static"
  | "bookie-mauve-static"
  | "bookie-emerald-static"
  | "bookie-blush-static"
  | "bookie-navy-static"
  | "bookie-amber-static"
  | "bookie-pearl-static"
  | "bookie-terracotta-static"
  | "bookie-charcoal-static"
  | "bookie-rose-static"
  | "bookie-olive-static"
  | "bookie-burgundy-static"
  | "bookie-jade-static"
  | "bookie-saffron-static"
  | "bookie-onyx-static"
  | "bookie-champagne-static"
  | "bookie-forest-static"
  | "bookie-coral-static"
  | "bookie-plum-static"
  | "bookie-azure-static"
  // Round 10 — 2 new designer iframe-static templates
  | "nikoh-static"
  | "wedding-is-coming-static"
  // Round 11 — Buklama paper-diorama / tunnel-book template
  | "buklama-static"
  // Round 12 — 2 designer watercolor wedding templates
  | "garden-arch-static"
  | "mountain-static"
  // Round 13 — celestial night-sky wedding template
  | "starry-night-static"
  // Round 14 — terracotta dried-floral wedding template
  | "terracotta-static"
  // Round 15 — paper-cut tunnel-book fairy-tale wedding template
  | "ertak-static"
  // Round 16 — twilight fairy-tale tunnel-book wedding template
  | "pari-static"
  // Round 17 — kids' circus / carnival birthday template
  | "sharlar-static"
  // Round 18 — sweet kids garden-party birthday template
  | "asalari-static"
  // Round 19 — celestial-library storybook birthday keepsake
  | "rumaysa-static"
  // Round 20 — autumn-marigold watercolor wedding invitation
  | "marigold-static"
  // Round 21 — peacock-motif emerald nikoh invitation
  | "tovus-static"
  // Round 22 — midnight-blue & gold "Tungi Saroy" wedding invitation
  | "midnight-palace-static"
  // Round 23 — "Yomg'irdan so'ng" night-to-day "rain clears into sunshine" wedding invitation
  | "yomgir-static"
  // Round 24 — "Yaylov" sunny-meadow wedding invitation
  | "yaylov-static"
  // Round 25 — "Sakura" sakura-falls wedding invitation
  | "sakura-static"
  // Round 26 — "Qog'oz ertagi" book-open storybook wedding invitation
  | "qogoz-static"
  // Round 27 — "Oltin yo'l" railway-journey wedding invitation
  | "oltin-yol-static"
  // Round 28 — "Cheksiz osmon" endless-sky wedding invitation
  | "osmon-static"
  // Round 29 — "To'rt fasl" four-seasons-scroll wedding invitation
  | "fasllar-static"
  // Round 30 — "Envelope" wax-seal envelope-reveal wedding invitation
  | "envelope-static";

export type TemplateFeature =
  | "countdown"
  | "story"
  | "gallery"
  | "schedule"
  | "timeline"
  | "dresscode"
  | "map"
  | "rsvp"
  | "guestbook"
  | "music"
  | "share"
  | "wishlist"
  | "agenda"
  | "speakers"
  | "quran"
  | "bio";

export interface Template {
  id: string;
  title: string;
  category: TemplateCategory;
  layout: TemplateLayout;
  variant: TemplateVariant;
  bg: string;
  ink: string;
  accent: string;
  font: string;
  price: number;
  badge?: "Bestseller" | "Premium" | "New";
  names: string;
  date: string;
  venue: string;
  photos: string[];
  features: TemplateFeature[];
  /** Optional gallery card art. When set, InvitationCard renders this
   *  webp as the card background instead of falling through to the
   *  procedural `layout` component. We're rolling card art out
   *  template-by-template; rows without `cardImage` keep their old
   *  procedural illustration. */
  cardImage?: string;
  /** Where to place the demo-name + date overlay over `cardImage`.
   *   • "bottom" (default) — overlay names/date in the lower portion.
   *     Right answer for most images that leave their lower half empty.
   *   • "none"              — no text overlay. Use when the image is
   *     fully illustrated edge-to-edge (e.g. w36 ertak's castle scene)
   *     and adding text would clash with the artwork. */
  cardOverlay?: "bottom" | "none";
  /** Hide from the PUBLIC catalog (gallery, landing, and the detail-page buy
   *  flow) while keeping the template in TEMPLATES so existing published
   *  invitations still resolve + render. Frontend-only — does NOT touch
   *  template_settings.is_public, which would also 404 live invitations of
   *  this template. Use to soft-launch / stage a template before going public. */
  unlisted?: boolean;
  // Optional payload-derived fields. Not set on the static catalog
  // entries below; GuestPage merges them on at render time so variants
  // can render the user's address / start time / story copy. Kept
  // optional so the existing seed entries don't all need updating.
  address?: string;
  /** Owner-supplied venue map link (Google Maps short URL, Yandex pin,
   *  etc.). Templates' map buttons open this URL verbatim when set;
   *  otherwise fall back to searching `address` on Google Maps. */
  mapUrl?: string;
  time?: string;
  story?: string;
  /** Promoted-from-custom payload fields (Phase C). GuestPage merges
   *  these from the invitation payload so variants + static-template
   *  wrappers can read t.dressCode / t.rsvpDeadline / etc. directly. */
  dressCode?: string;
  rsvpDeadline?: string;
  hashtag?: string;
  region?: string;
  /** Per-template custom field bag — payload.custom merged onto the
   *  template so variants read t.custom.<key> for manifest fields. */
  custom?: Record<string, unknown>;
  /** When set, iframe-based variants render their own <audio> element. */
  musicTrackId?: string | null;

  // Phase C — per-template field manifest. Drives the editor's dynamic
  // form: an `extraField` becomes a labelled <Input> / <Textarea> that
  // writes to payload.custom[key]; an `extraList` becomes a list editor
  // with add/remove rows that writes to payload.custom[key] = item[].
  // Templates read their own values back from payload.custom in their
  // variant component. Leaving the manifest undefined preserves the
  // original behaviour (no extra inputs in the editor).
  /** Which optional core fields the editor collects. Omit ⇒ all shown
   *  (default). A subset (e.g. []) suits a template that renders no event
   *  date / venue / map — the editor then shows only the name + extras and
   *  drops the "Joy" step. `names` is always shown. */
  coreFields?: CoreField[];
  extraFields?: ManifestField[];
  extraLists?: ManifestList[];
}

/** Optional built-in fields the editor can collect for a template.
 *  See Template.coreFields. */
export type CoreField = "date" | "time" | "venue" | "address" | "map";

/** Single scalar field exposed to the buyer. Type drives the input widget
 * the editor picks (text input / textarea / number / select). All values
 * land in payload.custom[key]. */
export interface ManifestField {
  /** Key under payload.custom — also the JSON key the template reads. */
  key: string;
  /** Uzbek label shown above the input in the editor. */
  label_uz: string;
  /** Optional Russian + English labels for the i18n editor. */
  label_ru?: string;
  label_en?: string;
  /** Input type. text_short → <Input>, text_long → <Textarea>, year/age
   *  → numeric <Input>, date/time → string <Input>, enum → <Select>. */
  type:
    | "text_short"
    | "text_long"
    | "year"
    | "age"
    | "date"
    | "time"
    | "phone"
    | "url"
    | "hashtag"
    | "name"
    | "enum";
  /** Placeholder / default value hint shown in the input. */
  placeholder?: string;
  /** Only for `enum` — the dropdown options. */
  options?: string[];
  /** If true, the editor flags the input as required. */
  required?: boolean;
  /** Pre-filled starting value in the editor when the invitation has no
   *  value for this key yet — lets a template ship editable default copy. */
  default?: string;
}

/** Repeating section. Editor renders an add-row / remove-row list.
 *  Each row is a record matching the `item` schema; the value stored at
 *  payload.custom[key] is an array of these rows. */
export interface ManifestList {
  key: string;
  label_uz: string;
  label_ru?: string;
  label_en?: string;
  /** Schema for each row — keys are the field name, values are the type. */
  item: Record<string, ManifestField["type"]>;
  /** Pretty label for each item field — keys match `item` keys. */
  itemLabelsUz?: Record<string, string>;
  /** Minimum / maximum row count enforced by the editor. */
  min?: number;
  max?: number;
  /** Default rows pre-filled in the editor when the invitation has no value
   *  for this key yet — e.g. a template's stock list the owner edits. */
  defaultRows?: Array<Record<string, string>>;
}

export const TEMPLATES: Template[] = [
  // ── Most recent designer-imported HTML templates surface first ────────
  // The iframe-static templates the user shipped most recently sit at
  // the top of the catalog so they show up first on the gallery + the
  // landing's featured strip. Render order = array order; the gallery
  // and landing both iterate TEMPLATES directly.

  // Round 30 — "Envelope" wax-seal envelope-reveal wedding invitation. A
  // candlelit wooden desk: splash is a sealed ivory envelope stamped with an
  // oxblood wax seal — tap the seal ⇒ the flap opens, the letter slides out
  // and unfolds in 3D, then the page continues. Hero: a letter-card with
  // shimmer gold names, localized date, "venue · HH:MM" line. Invitation: a
  // letter-card carrying invite_text + names + date. Sanoq: wax-seal
  // countdown tiles. Dastur: a programme timeline with wax-seal bullets (4
  // default events). Manzil: a venue photo card + Google/Yandex/Telegram
  // buttons. Ma'lumot: a dress-code palette card + an RSVP phone card.
  // Tilaklar: a paper-slip guestbook wall. Footer: a candlelit scene with a
  // mini envelope. In-iframe UZ/EN/RU switcher + music. Flat iframe-static
  // page at /templates/envelope.html. No parents-split fields, no hashtag —
  // this design has neither. The HTML reads names/date/time/venue/address/
  // map_url + invite_text, dress_text, rsvp_text, phone, flowers_note,
  // telegram_url, default_lang, and an optional ?events= list (JSON) —
  // owner clearing it ([]) hides the programme section; untouched keeps the
  // four default events.
  {
    id: "w47", title: "Envelope", category: "wedding",
    layout: "envelope-preview", variant: "envelope-static",
    bg: "#f4e8d2", ink: "#3a2233", accent: "#7a2230",
    font: "'Prata', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/envelope.webp",
    names: "Shahzod & Robiya", date: "24.10.2026", venue: "Navruz Hall bazm zali",
    address: "Toshkent sh., Afrosiyob ko'chasi, 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "invite_text",
        label_uz: "Taklifnoma matni (bo'sh — yashiriladi)",
        label_ru: "Текст приглашения (пусто — скрыть)",
        label_en: "Invitation text (empty hides it)",
        type: "text_long",
        default: "Sizni hayotimizdagi eng baxtli kun — nikoh to'yimiz quvonchini biz bilan baham ko'rishga taklif etamiz. Kelishingiz bayramimizga o'zgacha fayz bag'ishlaydi.",
      },
      {
        key: "dress_text",
        label_uz: "Dress-kod (bo'sh — blok yashiriladi)",
        label_ru: "Дресс-код (пусто — скрыть блок)",
        label_en: "Dress code (empty hides the block)",
        type: "text_long",
        default: "Tantanali libos. Sizni palitramiz ohanglarida ko'rishdan mamnun bo'lamiz:",
      },
      {
        key: "rsvp_text",
        label_uz: "Tasdiqlash matni (bo'sh — yashiriladi)",
        label_ru: "Текст подтверждения (пусто — скрыть)",
        label_en: "RSVP note (empty hides it)",
        type: "text_long",
        default: "Iltimos, ishtirokingizni quyidagi raqam orqali tasdiqlang:",
      },
      {
        key: "phone",
        label_uz: "Telefon raqami (bo'sh — yashiriladi)",
        label_ru: "Номер телефона (пусто — скрыть)",
        label_en: "Phone number (empty hides it)",
        type: "text_short",
        default: "+998 90 123-45-67",
      },
      {
        key: "flowers_note",
        label_uz: "Yakuniy iliq so'z (bo'sh — yashiriladi)",
        label_ru: "Тёплая строка в конце (пусто — скрыть)",
        label_en: "Closing warm note (empty hides it)",
        type: "text_long",
        default: "Gullar o'rniga — iliq so'zlar: tabassumingiz va ezgu tilaklaringiz biz uchun eng aziz sovg'adir.",
      },
      {
        key: "telegram_url",
        label_uz: "Telegram havolasi (bo'sh — tugma ko'rinmaydi)",
        label_ru: "Ссылка Telegram (пусто — кнопка скрыта)",
        label_en: "Telegram link (empty hides the button)",
        type: "text_short",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Oqshom dasturi (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа вечера (пусто — скрыть)",
        label_en: "Evening programme (empty hides the section)",
        item: { time: "time", title: "text_short" },
        itemLabelsUz: { time: "Vaqt", title: "Voqea" },
        min: 0,
        max: 8,
        defaultRows: [
          { time: "16:30", title: "Mehmonlar yig'ilishi" },
          { time: "17:00", title: "Kelin-kuyov kutib olinadi" },
          { time: "18:00", title: "Bayram bazmi" },
          { time: "22:00", title: "Kuzatuv" },
        ],
      },
    ],
  },

  // Round 29 — "To'rt fasl" (Four Seasons) wedding invitation. One
  // continuous season scroll: a bare tree on a hill lives through spring,
  // summer, autumn and winter as the guest scrolls, the body background
  // lerping through four pastel stops keyed to the live section midpoints.
  // Splash: circle-monogram initials ("T · M"), kicker, cursive names, an
  // open button over a pre-baked (no runtime blur) spring backdrop — Open
  // ⇒ fade, unlock, music starts. Spring hero: seeded falling petals,
  // glowing cursive names, numeric date, "CITY · venue" line. Parallax
  // transition/divider bands carry mixed petal/seed/leaf/snowflake
  // particles between seasons. Summer: a wreath-framed invitation card
  // (heading + invite_text + names + localized date/city) and a "Kun
  // dasturi" timeline with four season-colored dots (4 default events,
  // owner events cycle the same four colors). Autumn: a venue card (name +
  // address + map), a details card (Kiyim with four season swatches,
  // Tasdiqlash tel:-linkified, Gullar — any-empty hides its row, all-three
  // empty drops the whole card), and a tovus-style guestbook. Winter:
  // full-viewport night sky, falling snow, four glass countdown tiles,
  // localized date + "venue · HH:MM" line. Finale: pastel gradient, a
  // bobbing petal, footer_note, cursive names, numeric date + "mehr
  // bilan". In-iframe UZ/EN/RU switcher + music. Flat iframe-static page
  // at /templates/fasllar.html. No parents-split fields, no hashtag — this
  // design has neither. The HTML reads names/date/time/venue/address/
  // map_url + invite_text, three guest-info rows (dress/rsvp/flowers), a
  // footer note, default_lang, and an optional ?events= timeline (JSON) —
  // owner clearing it ([]) hides the "Kun dasturi" section + its header +
  // the divider-summer band above it (the scenery transition bands stay);
  // untouched keeps the four default events.
  {
    id: "w46", title: "To'rt fasl", category: "wedding",
    layout: "fasllar-preview", variant: "fasllar-static",
    bg: "#fdf6ef", ink: "#4a3b33", accent: "#a2506f",
    font: "'Vollkorn', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/fasllar.webp",
    names: "Farrux & Gulnoza", date: "26.09.2026", venue: "Versal to'yxonasi",
    address: "Toshkent sh., Bog' ko'chasi 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "invite_text",
        label_uz: "Taklifnoma matni (bo'sh — yashiriladi)",
        label_ru: "Текст приглашения (пусто — скрыть)",
        label_en: "Invitation text (empty hides it)",
        type: "text_long",
        default: "Gullash, jazirama, xazonrezgi va ilk qor orqali — biz birga qadam tashlaymiz. Umumiy tariximiz boshlanadigan kunning quvonchini biz bilan baham ko'rishga taklif etamiz.",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Istalgan fasl palitrasidagi libos",
      },
      {
        key: "info_rsvp",
        label_uz: "Bilib qo'ying — Tasdiqlash (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подтверждение (пусто — скрыть)",
        label_en: "Guest info — RSVP (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 12-sentabrgacha javob bering — +998 90 123-45-67",
      },
      {
        key: "info_flowers",
        label_uz: "Bilib qo'ying — Gullar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Цветы (пусто — скрыть)",
        label_en: "Guest info — Flowers (empty hides the row)",
        type: "text_long",
        default: "Guldastalar o'rniga — iliq so'zlar: quyida tilak qoldiring",
      },
      {
        key: "footer_note",
        label_uz: "Xulosa qatori (bo'sh — yashiriladi)",
        label_ru: "Заключительная строка (пусто — скрыть)",
        label_en: "Closing line (empty hides it)",
        type: "text_long",
        default: "Qish doimo bahor bilan tugaydi —\nbizning bahorimiz esa sizdan boshlanadi.",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Kun dasturi (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа дня (пусто — скрыть)",
        label_en: "Day program (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 28 — "Cheksiz osmon" (Endless Sky) wedding invitation. The whole
  // page is one continuous sky — four stitched sky tiles run from splash
  // to footer, blending through nine day-to-night colour stops as the
  // guest scrolls, with parallax clouds and nine drifting objects (whale-
  // cloud, cottage, airship, birds, plane, balloons, island, kite) crossing
  // at their own speeds. Splash: blurred sky + balloon, Yeseva "T & M"
  // monogram, Borel full names, glass open button — Open ⇒ fade, unlock,
  // music starts. Hero: two cloud parallax layers, Borel kicker, shimmer
  // two-line names, localized date, "Osmonga ko'tarilamiz" hint. Glass
  // cards: Taklifnoma (wreath-ringed invite copy + names + date +
  // parents_line), Tadbirlar (4 default events, gold-dot spine), Manzil
  // (venue + address + map), Ma'lumot (3 guest-info bullet rows), Tilaklar
  // (tovus-style guestbook). Sanoq: sunset countdown scene. Footer: 14
  // seeded twinkle stars, initials, date + city. Flat iframe-static page at
  // /templates/osmon.html. No parents-split fields, no hashtag, no footer
  // free-text slot — this design has none. The HTML reads
  // names/date/time/venue/address/map_url + parents_line, three guest-info
  // rows, default_lang, and an optional ?events= list (JSON) — owner
  // clearing it ([]) hides the Tadbirlar card + section; untouched keeps
  // the four default events.
  {
    id: "w45", title: "Cheksiz osmon", category: "wedding",
    layout: "osmon-preview", variant: "osmon-static",
    bg: "#cbecfe", ink: "#274565", accent: "#3d6a99",
    font: "'Yeseva One', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/osmon.webp",
    names: "Sevara & Islom", date: "12.09.2026", venue: "Navro'z to'yxonasi",
    address: "Toshkent sh., Chilonzor tumani, Bunyodkor shoh ko'chasi 12A",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "parents_line",
        label_uz: "Ota-onalar qatori (bo'sh — yashiriladi)",
        label_ru: "Строка родителей (пусто — скрыть)",
        label_en: "Parents line (empty hides it)",
        type: "text_short",
        default: "Hurmat bilan: Karimovlar va Rahimovlar oilalari",
      },
      {
        key: "info_rsvp",
        label_uz: "Ma'lumot — RSVP (bo'sh — yashiriladi)",
        label_ru: "Информация — RSVP (пусто — скрыть)",
        label_en: "Info — RSVP (empty hides the row)",
        type: "text_long",
        default: "Kelishingizni 5-sentabrgacha tasdiqlashingizni so'raymiz: +998 90 123-45-67",
      },
      {
        key: "info_dress",
        label_uz: "Ma'lumot — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Информация — Дресс-код (пусто — скрыть)",
        label_en: "Info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Dress-kod: yengil, osmon va bahor ranglari",
      },
      {
        key: "info_gifts",
        label_uz: "Ma'lumot — Sovg'alar (bo'sh — yashiriladi)",
        label_ru: "Информация — Подарки (пусто — скрыть)",
        label_en: "Info — Gifts (empty hides the row)",
        type: "text_long",
        default: "Eng qimmatli sovg'a — do'stlaringiz davrasidagi samimiy tabrigingiz",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Tadbirlar (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Мероприятия (пусто — скрыть)",
        label_en: "Events (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 27 — "Oltin yo'l" (The Golden Line) railway-journey wedding
  // invitation (splash: blurred hero backdrop, dashed-ring ticket-punch
  // circle with the couple's initials monogram, cursive names, ticket-
  // shaped open button with punched side-notches; Open ⇒ splash away,
  // scroll unlocks, music starts; a dashed railway spine runs the left
  // edge with a tiny train marker tracking scroll progress; hero
  // "Departure" with parallax hero art + drifting light motes + shimmer
  // names; three scenery journey strips with mile badges; invitation
  // rendered as a train ticket (perforated stub, invite copy, names,
  // date, departure line, class/seat/route footer); timetable as a
  // line-diagram stop list; venue card with a note + map; guest info as a
  // punched luggage tag (dress/arrival/gifts/contact, tel:-linkified);
  // wishes as telegraph-style "telegrams"; countdown as a terminus scene
  // with bunting sway + steam drift + lantern flicker; footer as a dusk
  // platform with the train at rest; scroll-driven body background
  // through five warm stops; in-iframe UZ/EN/RU switcher + music). Flat
  // iframe-static page at /templates/oltin-yol.html. No parents fields,
  // no hashtag — this design has neither. The HTML reads
  // names/date/time/venue/address/map_url + a venue note, four guest-info
  // rows, a footer note, default_lang, and an optional ?events= timetable
  // (JSON) — when the owner clears the timetable the Timetable section +
  // its header + spine dot + the bunting strip above it all hide (the
  // journey strips are scenery and stay); untouched keeps the five
  // default stops.
  {
    id: "w44", title: "Oltin yo'l", category: "wedding",
    layout: "oltin-yol-preview", variant: "oltin-yol-static",
    bg: "#fbf3e2", ink: "#3f2d20", accent: "#b3552f",
    font: "'Podkova', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/oltin-yol.webp",
    names: "Aziz & Feruza", date: "27.09.2026", venue: "Navro'z to'yxonasi",
    address: "Toshkent sh., Bog'ishamol ko'chasi 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Строка под адресом (пусто — скрыть)",
        label_en: "Note under the address (empty hides it)",
        type: "text_short",
        default: "Darvozalar 16:30 da ochiladi — avtoturargohdan bog'gacha chiroqlar kuzatib boradi.",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Bog' bayrami libosi — iliq kuz ranglari yo'lda eng chiroyli.",
      },
      {
        key: "info_arrival",
        label_uz: "Bilib qo'ying — Jo'nash vaqti (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Время отправления (пусто — скрыть)",
        label_en: "Guest info — Departure time (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 16:45 gacha yetib keling — marosim o'z vaqtida platformadan jo'naydi.",
      },
      {
        key: "info_gifts",
        label_uz: "Bilib qo'ying — Sovg'alar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подарки (пусто — скрыть)",
        label_en: "Guest info — Gifts (empty hides the row)",
        type: "text_long",
        default: "Tashrifingiz — eng katta sovg'a; gullar bepul yuradi, qolgani ko'nglingizdan.",
      },
      {
        key: "info_contact",
        label_uz: "Bilib qo'ying — Bekat boshlig'i (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Начальник станции (пусто — скрыть)",
        label_en: "Guest info — Contact (empty hides the row)",
        type: "text_long",
        default: "Aziz (kuyov jo'rasi) · +998 90 000 00 00",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "safar tugadi — yangisi boshlanadi",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Kun jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Расписание дня (пусто — скрыть)",
        label_en: "Day timetable (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 26 — "Qog'oz ertagi" (Paper Meadow storybook) book-open wedding
  // invitation (splash: a closed 3D book on a desk gradient, cover with a
  // gold title-plate — kicker/names/date-dots — and the "siz uchun" inner
  // page behind it; Open ⇒ cover page-turns open ⇒ scroll unlocks ⇒ music
  // starts; hero scene with sun/bird/5 bobbing petals/hills/arch scene-
  // centered parallax + shimmer names; page-turn strip with two scenery
  // cards; frame-card invitation with the invite sentence (no names —
  // those live on cover/hero/footer); dashed-spine ceremony timeline;
  // venue with map + doors/parking note; guest info grid (dress code,
  // tel:-linkified contact); wishes wall; countdown scene with its own
  // paper card; gold-bordered back-cover footer with shimmer names +
  // date-dots + italic closing note; scroll-driven desk-gradient background
  // through three stops; in-iframe UZ/EN/RU switcher + music). Flat
  // iframe-static page at /templates/qogoz.html. No parents fields, no
  // hashtag — this design has neither. The HTML reads
  // names/date/time/venue/address/map_url + venue note, two info rows,
  // footer note, default_lang, and an optional ?events= timeline (JSON) —
  // when the owner clears the timeline the Events section + header hides
  // (the page-turn strip cards are scenery and stay); untouched keeps the
  // four sample events.
  {
    id: "w43", title: "Qog'oz ertagi", category: "wedding",
    layout: "qogoz-preview", variant: "qogoz-static",
    bg: "#f6ecd6", ink: "#4a3a28", accent: "#c9a24b",
    font: "'Amarante', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/qogoz.webp",
    names: "Doniyor & Zarina", date: "26.09.2026", venue: "Navro'z Saroyi",
    address: "Toshkent sh., Bog' ko'chasi 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Строка под адресом (пусто — скрыть)",
        label_en: "Note under the address (empty hides it)",
        type: "text_short",
        default: "Eshiklar 16:00 da ochiladi · hovlida avtoturargoh",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Bog' pastellari va yengil matolar — oqshom iliq bo'ladi.",
      },
      {
        key: "info_contact",
        label_uz: "Bilib qo'ying — Savollar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Вопросы (пусто — скрыть)",
        label_en: "Guest info — Questions (empty hides the row)",
        type: "text_long",
        default: "Aziza, tadbir tashkilotchisi — +998 90 000 00 00",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Nihoya — va ibtido.\nMehr, qog'oz va yelim bilan yaratildi.",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "To'y jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Свадебная программа (пусто — скрыть)",
        label_en: "Wedding timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 25 — "Sakura" (sakura falls) rose-on-cream wedding invitation
  // (splash blurred-hero monogram open; hero with a swaying branch overlay
  // + 14 falling petals, shimmer names, dateLong band, location line;
  // meadow-strip scenery; invitation frame card; time-only ceremony
  // timeline; venue with map + doors-open note; guest info grid (dress
  // code, tel:-linkified contact, good-to-know note); wishes wall;
  // waterfall countdown with drifting mist + petals; sunset-pool footer;
  // scroll-driven body background through five bloom-color stops;
  // in-iframe UZ/EN/RU switcher + music). Flat iframe-static page at
  // /templates/sakura.html. No parents fields, no hashtag — this design
  // has neither. The HTML reads names/date/time/venue/address/map_url +
  // venue note, three info rows, footer note, default_lang, and an
  // optional ?events= timeline (JSON) — when the owner clears the
  // timeline the Events section + header hides (the meadow strips are
  // scenery art and stay); untouched keeps the four sample events.
  {
    id: "w42", title: "Sakura", category: "wedding",
    layout: "sakura-preview", variant: "sakura-static",
    bg: "#fdf6f0", ink: "#3f3340", accent: "#e58aa6",
    font: "'Shippori Mincho', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/sakura.webp",
    names: "Kamola & Sarvar", date: "17.04.2027", venue: "Navro'z Saroyi",
    address: "Toshkent sh., Bog'ishamol ko'chasi 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Строка под адресом (пусто — скрыть)",
        label_en: "Note under the address (empty hides it)",
        type: "text_short",
        default: "Eshiklar 16:30 da ochiladi",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Bahor pastellari va bog' bazmi libosi",
      },
      {
        key: "info_contact",
        label_uz: "Bilib qo'ying — Aloqa (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Контакты (пусто — скрыть)",
        label_en: "Guest info — Contact (empty hides the row)",
        type: "text_long",
        default: "+998 90 123 45 67 · Aziza\n+998 91 765 43 21 · Bekzod",
      },
      {
        key: "info_note",
        label_uz: "Bilib qo'ying — Eslatma (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Заметка (пусто — скрыть)",
        label_en: "Guest info — Good to know (empty hides the row)",
        type: "text_long",
        default: "Bazm suv bo'yida o'tadi — oqshom salqin bo'lishi mumkin, yengil ustki kiyim oling.",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_short",
        default: "Gullar ostida ko'rishguncha",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "To'y jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Свадебная программа (пусто — скрыть)",
        label_en: "Wedding timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 24 — "Yaylov" sunny-meadow wedding invitation (splash blurred-hero
  // monogram open; hero meadow art with parallax clouds + 14 falling
  // petals; transition strip with a crossing balloon; floral-frame
  // invitation card; two divider strips; date-less time-only ceremony
  // timeline; venue with map; guest info grid (dress code with swatch
  // dots, arrival, questions with tel: linkify); wishes wall; full-height
  // sky countdown with parallax clouds + drifting mist; sunset footer;
  // scroll-driven body background through six sky-color stops; in-iframe
  // UZ/EN/RU switcher + music). Flat iframe-static page at
  // /templates/yaylov.html. No parents fields, no hashtag — this design
  // has neither. The HTML reads names/date/time/venue/address/map_url +
  // venue note, three info rows, footer note, default_lang, and an
  // optional ?events= timeline (JSON) — when the owner clears the
  // timeline the Events section + header hides (the two divider strips
  // around it are scenery art and stay); untouched keeps the four
  // sample events.
  {
    id: "w41", title: "Yaylov", category: "wedding",
    layout: "yaylov-preview", variant: "yaylov-static",
    bg: "#fdf8ec", ink: "#33502b", accent: "#6faf4f",
    font: "'Kaisei Decol', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/yaylov.webp",
    names: "Bekzod & Nigora", date: "26.09.2026", venue: "Navro'z Saroyi",
    address: "Toshkent sh., Bog'ishamol ko'chasi 12",
    time: "16:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Строка под адресом (пусто — скрыть)",
        label_en: "Note under the address (empty hides it)",
        type: "text_short",
        default: "To'yxona · darvoza oldida bepul avtoturargoh",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Yengil bog' ranglari — erkin va bayramona uslub",
      },
      {
        key: "info_arrival",
        label_uz: "Bilib qo'ying — Kelish vaqti (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Время прибытия (пусто — скрыть)",
        label_en: "Guest info — Arrival time (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 15:45 gacha joy oling — marosim o'z vaqtida boshlanadi",
      },
      {
        key: "info_contact",
        label_uz: "Bilib qo'ying — Savollar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Вопросы (пусто — скрыть)",
        label_en: "Guest info — Contact (empty hides the row)",
        type: "text_long",
        default: "Aziz — +998 90 123 45 67\nNigora — +998 93 555 12 34",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Hayotimizning eng quyoshli kunini siz bilan baham ko'rishga oshiqamiz.",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "To'y jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Свадебная программа (пусто — скрыть)",
        label_en: "Wedding timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 23 — "Yomg'irdan so'ng" (After the Rain) night-to-day wedding
  // invitation (splash blurred-night monogram open; hero night village with
  // rain streaks; scroll-driven background interpolates through night ->
  // dawn -> day; dawn invitation card + parents frame; ceremony timeline;
  // day venue with map; guest info grid; wishes wall; countdown under a
  // drifting-cloud sky; in-iframe UZ/EN/RU switcher + music). Flat
  // iframe-static page at /templates/yomgir.html. The HTML reads
  // names/date/time/venue/address/map_url + owner parents, four info rows,
  // footer note, default_lang, and an optional ?events= timeline (JSON) —
  // when the owner clears the timeline the whole section (header + both
  // bracketing dividers) hides; untouched keeps the three sample events.
  {
    id: "w40", title: "Yomg'irdan so'ng", category: "wedding",
    layout: "yomgir-preview", variant: "yomgir-static",
    bg: "#0a1428", ink: "#f7ecd9", accent: "#d98e3f",
    font: "'Milonga', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/yomgir.webp",
    names: "Sardor & Malika", date: "16.10.2026", venue: "Navro'z Saroyi",
    address: "Toshkent sh., Alisher Navoiy shoh ko'chasi 12",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "groom_parents",
        label_uz: "Kuyov ota-onasi (bo'sh — yashiriladi)",
        label_ru: "Родители жениха (пусто — скрыть)",
        label_en: "Groom's parents (empty hides the line)",
        type: "text_short",
        default: "Bahodir va Nilufar Karimovlar",
      },
      {
        key: "bride_parents",
        label_uz: "Kelin ota-onasi (bo'sh — yashiriladi)",
        label_ru: "Родители невесты (пусто — скрыть)",
        label_en: "Bride's parents (empty hides the line)",
        type: "text_short",
        default: "Rustam va Gulnora Yusupovlar",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Bayramona va bejirim libos — oqshom qisman ochiq havoda o'tadi.",
      },
      {
        key: "info_arrival",
        label_uz: "Bilib qo'ying — Kelish vaqti (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Время прибытия (пусто — скрыть)",
        label_en: "Guest info — Arrival time (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 16:45 gacha joy oling — marosim o'z vaqtida boshlanadi.",
      },
      {
        key: "info_gifts",
        label_uz: "Bilib qo'ying — Sovg'alar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подарки (пусто — скрыть)",
        label_en: "Guest info — Gifts (empty hides the row)",
        type: "text_long",
        default: "Tashrifingiz va iliq tilaklaringiz — eng katta sovg'a.",
      },
      {
        key: "info_contact",
        label_uz: "Bilib qo'ying — Savollar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Вопросы (пусто — скрыть)",
        label_en: "Guest info — Contact (empty hides the row)",
        type: "text_long",
        default: "Savollar uchun Azizga qo'ng'iroq qiling: +998 90 123 45 67",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Har yomg'irdan so'ng quyosh chiqadi — eng yorug' kunimizni siz bilan baham ko'rishga oshiqamiz.",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Tantanalar jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа торжеств (пусто — скрыть)",
        label_en: "Events timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 22 — "Tungi Saroy" (Midnight Palace) midnight-blue & gold wedding
  // invitation (deep navy + gold; splash open with monogram, hero with
  // twin shimmer-gold name lines, twinkling firefly dots on hero + countdown,
  // events timeline, venue with map, guest info grid, wishes wall,
  // countdown, in-iframe UZ/EN/RU switcher + music). Flat iframe-static
  // page at /templates/midnight-palace.html. The HTML reads
  // names/date/time/venue/address/map_url + owner parents, venue note,
  // events note, four info rows, hashtag, footer note, default_lang, and an
  // optional ?events= timeline (JSON) — when the owner clears the timeline
  // the whole section (incl. header + both divider bands) hides; untouched
  // keeps the five sample events.
  {
    id: "w39", title: "Tungi Saroy", category: "wedding",
    layout: "midnight-palace-preview", variant: "midnight-palace-static",
    bg: "#0b1026", ink: "#c6cde8", accent: "#d8b65c",
    font: "'Marcellus', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/midnight-palace.webp",
    names: "Dilnoza & Jahongir", date: "12.12.2026", venue: "Navro'z Saroyi",
    address: "Samarqand sh., Registon ko'chasi 12",
    time: "19:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "groom_parents",
        label_uz: "Kuyov ota-onasi haqida (bo'sh — yashiriladi)",
        label_ru: "О родителях жениха (пусто — скрыть)",
        label_en: "About the groom's parents (empty hides the line)",
        type: "text_short",
        default: "Gulnora va Rustam Karimovlarning o'g'li",
      },
      {
        key: "bride_parents",
        label_uz: "Kelin ota-onasi haqida (bo'sh — yashiriladi)",
        label_ru: "О родителях невесты (пусто — скрыть)",
        label_en: "About the bride's parents (empty hides the line)",
        type: "text_short",
        default: "Dilnoza va Bobur Rahimovlarning qizi",
      },
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Примечание под адресом (пусто — скрыть)",
        label_en: "Note under the venue (empty hides it)",
        type: "text_short",
        default: "Sharqiy darvoza oldida keng avtoturargoh",
      },
      {
        key: "events_note",
        label_uz: "Tantanalar sanasi (bo'sh — yashiriladi)",
        label_ru: "Строка с датами торжеств (пусто — скрыть)",
        label_en: "Events date-range line (empty hides it)",
        type: "text_short",
        default: "10 — 13 DEKABR · SAMARQAND",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the row)",
        type: "text_long",
        default: "Bayramona libos — tungi ko'k, zumrad va tilla ranglarda. Atlas va adras alohida qadrlanadi.",
      },
      {
        key: "info_weather",
        label_uz: "Bilib qo'ying — Ob-havo (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Погода (пусто — скрыть)",
        label_en: "Guest info — Weather (empty hides the row)",
        type: "text_long",
        default: "Tantanalar dekabrning salqin oqshomlarida davom etadi — hovli uchun issiq kiyim oling.",
      },
      {
        key: "info_stay",
        label_uz: "Bilib qo'ying — Mehmonxona va yo'l (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Проживание и дорога (пусто — скрыть)",
        label_en: "Guest info — Stay & travel (empty hides the row)",
        type: "text_long",
        default: "Uzoqdan keluvchi mehmonlar uchun xonalar band qilingan. So'rov bo'yicha aeroportdan kutib olamiz.",
      },
      {
        key: "info_rsvp",
        label_uz: "Bilib qo'ying — Tasdiqlash (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подтверждение (пусто — скрыть)",
        label_en: "Guest info — RSVP (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 25-noyabrgacha tasdiqlang · +998 90 123 45 67",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Sizning tashrifingiz — biz uchun eng katta sovg'a.",
      },
      {
        key: "hashtag",
        label_uz: "Hashtag (bo'sh — yashiriladi)",
        label_ru: "Хэштег (пусто — скрыть)",
        label_en: "Hashtag (empty hides it)",
        type: "hashtag",
        placeholder: "#TimurMadina2026",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Tantanalar jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа торжеств (пусто — скрыть)",
        label_en: "Events timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 21 — "Tovus" (Peacock) emerald-night nikoh invitation (zumrad
  // green + gold + teal; splash open, hero with Parisienne name lines,
  // feathered-frame centrepiece, ceremony timeline, venue with map, guest
  // info grid, wishes wall, countdown, floating particles, in-iframe
  // UZ/EN/RU switcher + music). Flat iframe-static page at
  // /templates/tovus.html. The HTML reads names/date/time/venue/address/
  // map_url + owner parents, quote, four info rows, footer note,
  // default_lang, and an optional ?events= timeline (JSON) — when the
  // owner clears the timeline the whole section hides; untouched keeps
  // the two sample events.
  {
    id: "n03", title: "Tovus", category: "nikoh",
    layout: "tovus-preview", variant: "tovus-static",
    bg: "#0C1E1A", ink: "#F6F1E7", accent: "#C6A24A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/tovus.webp",
    names: "Avazbek & Anora", date: "14.08.2026", venue: "Beksaroy",
    address: "Toshkent sh., Yunusobod tumani",
    time: "16:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "groom_parents",
        label_uz: "Kuyov haqida izoh (bo'sh — yashiriladi)",
        label_ru: "Строка о женихе (пусто — скрыть)",
        label_en: "Groom's line (empty hides it)",
        type: "text_short",
        default: "Karimovlar xonadonining o'g'li",
      },
      {
        key: "bride_parents",
        label_uz: "Kelin haqida izoh (bo'sh — yashiriladi)",
        label_ru: "Строка о невесте (пусто — скрыть)",
        label_en: "Bride's line (empty hides it)",
        type: "text_short",
        default: "Yusupovlar xonadonining qizi",
      },
      {
        key: "quote",
        label_uz: "Iqtibos (bo'sh — yashiriladi)",
        label_ru: "Цитата (пусто — скрыть)",
        label_en: "Quote (empty hides it)",
        type: "text_short",
        default: "Ikki qalb — bir yurak, abadiyatning ibtidosi.",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Guest info — Dress code (empty hides the cell)",
        type: "text_long",
        default: "Bayramona libos — zumrad, feruza va tilla ranglarda",
      },
      {
        key: "info_timing",
        label_uz: "Bilib qo'ying — Vaqtlar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Время (пусто — скрыть)",
        label_en: "Guest info — Timing (empty hides the cell)",
        type: "text_long",
        default: "Nikoh 16:00 · Bazm 19:00",
      },
      {
        key: "info_rsvp",
        label_uz: "Bilib qo'ying — Tasdiqlash (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подтверждение (пусто — скрыть)",
        label_en: "Guest info — RSVP (empty hides the cell)",
        type: "text_long",
        default: "Iltimos, 1-avgustgacha tasdiqlang",
      },
      {
        key: "info_parking",
        label_uz: "Bilib qo'ying — Avtoturargoh (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Парковка (пусто — скрыть)",
        label_en: "Guest info — Parking (empty hides the cell)",
        type: "text_long",
        default: "Asosiy darvoza oldida avtoturargoh mavjud",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Mehr va minnatdorlik ila — sizni ko'rishdan mamnun bo'lamiz.",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Marosim jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа торжества (пусто — скрыть)",
        label_en: "Ceremony timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 6,
      },
    ],
  },

  // Round 20 — "Marigold" autumn watercolor wedding (cream + terracotta +
  // sage; flowering-arch hero, marigold garland + frame + wreath medallion,
  // splash monogram, four-event timeline, "good to know" card, wishes wall,
  // circular countdown, falling petals, in-iframe UZ/EN/RU switcher +
  // music). Flat iframe-static page at /templates/marigold.html. The HTML
  // reads names/date/time/venue/address/map_url + owner parents, four info
  // rows, hashtag, footer note, venue note, default_lang, and an optional
  // ?events= timeline (JSON) — when the owner clears the timeline the whole
  // section hides; untouched keeps the four sample events.
  {
    id: "w38", title: "Marigold", category: "wedding",
    layout: "marigold-preview", variant: "marigold-static",
    bg: "#f5ead8", ink: "#201e1d", accent: "#c67139",
    font: "'Caprasimo', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/marigold.webp",
    names: "Temur & Madina", date: "12.12.2026", venue: "Bog' Saroyi",
    address: "Toshkent sh., Bog'ishamol ko'chasi 14",
    time: "16:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "guestbook", "share", "music"],
    extraFields: [
      {
        key: "bride_parents",
        label_uz: "Kelin ota-onasi (bo'sh — yashiriladi)",
        label_ru: "Родители невесты (пусто — скрыть)",
        label_en: "Bride's parents (empty hides the line)",
        type: "text_short",
        default: "Karim va Nodira Karimovlar",
      },
      {
        key: "groom_parents",
        label_uz: "Kuyov ota-onasi (bo'sh — yashiriladi)",
        label_ru: "Родители жениха (пусто — скрыть)",
        label_en: "Groom's parents (empty hides the line)",
        type: "text_short",
        default: "Rahim va Malika Rahimovlar",
      },
      {
        key: "venue_note",
        label_uz: "Manzil ostidagi izoh (bo'sh — yashiriladi)",
        label_ru: "Примечание под адресом (пусто — скрыть)",
        label_en: "Note under the venue (empty hides it)",
        type: "text_short",
        default: "Asosiy darvoza oldida avtoturargoh",
      },
      {
        key: "info_dress",
        label_uz: "Bilib qo'ying — Kiyim (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Дресс-код (пусто — скрыть)",
        label_en: "Good to know — Dress code (empty hides the row)",
        type: "text_long",
        default: "Iliq ranglar — qahrabo, terrakota va o'tloq yashili.",
      },
      {
        key: "info_stay",
        label_uz: "Bilib qo'ying — Mehmonxona (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Проживание (пусто — скрыть)",
        label_en: "Good to know — Stay (empty hides the row)",
        type: "text_long",
        default: "Grand Park mehmonxonasida xonalar band qilingan — ismimizni ayting.",
      },
      {
        key: "info_gifts",
        label_uz: "Bilib qo'ying — Sovg'alar (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подарки (пусто — скрыть)",
        label_en: "Good to know — Gifts (empty hides the row)",
        type: "text_long",
        default: "Ishtirokingiz va duoyingiz — biz uchun eng qimmat sovg'a.",
      },
      {
        key: "info_rsvp",
        label_uz: "Bilib qo'ying — Tasdiqlash (bo'sh — yashiriladi)",
        label_ru: "Полезно знать — Подтверждение (пусто — скрыть)",
        label_en: "Good to know — RSVP (empty hides the row)",
        type: "text_long",
        default: "Iltimos, 20-noyabrgacha tasdiqlang — +998 90 123 45 67",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy tilak (footer)",
        label_ru: "Заключительное пожелание (футер)",
        label_en: "Closing note (footer)",
        type: "text_long",
        default: "Sizni bag'rimizga olishga oshiqamiz — gullarga burkangan hovlidan muhabbat bilan.",
      },
      {
        key: "hashtag",
        label_uz: "Hashtag (bo'sh — yashiriladi)",
        label_ru: "Хэштег (пусто — скрыть)",
        label_en: "Hashtag (empty hides it)",
        type: "hashtag",
        placeholder: "#TemurVaMadina",
      },
      {
        key: "default_lang",
        label_uz: "Boshlang'ich til",
        label_ru: "Язык по умолчанию",
        label_en: "Default language",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "events",
        label_uz: "Marosim jadvali (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа торжества (пусто — скрыть)",
        label_en: "Celebration timeline (empty hides the section)",
        item: { time: "time", place: "text_short", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", place: "Joy", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 6,
      },
    ],
  },

  // Round 19 — celestial-library "storybook" birthday keepsake (midnight
  // blue + antique gold + lavender; tunnel-book arches, drifting books /
  // balloons / butterflies, a splash monogram, a "letter from the moon",
  // four year-wish chapters, and a shared wishes wall). Flat iframe-static
  // page at /templates/rumaysa.html. Pure keepsake — NO date / venue /
  // countdown / map by design; rumaysa.html reads names (first segment) +
  // custom letter / closing_line / chapters + ?audio. The wishes wall
  // round-trips to the backend guestbook via ?slug.
  {
    id: "b13", title: "Four wishes", category: "birthday",
    layout: "rumaysa-preview", variant: "rumaysa-static",
    bg: "#1A1F3A", ink: "#F2EBDB", accent: "#D9C383",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/b13.webp",
    // Staged: hidden from the public catalog for now. Live published
    // invitations (e.g. /m7tdc/mahin) still resolve + render — this is a
    // frontend-only unlist, not template_settings.is_public.
    unlisted: true,
    names: "Rumaysa", date: "14.07.2026", venue: "Tun kutubxonasi",
    address: "Toshkent sh.",
    time: "18:00",
    photos: [],
    // rumaysa.html reads names (first segment → name + monogram) + the
    // custom letter / closing_line / chapters + ?audio. Date/venue are
    // catalog-card flavour only — the page renders no event logistics, so
    // the editor collects just the name: coreFields:[] hides date/venue/map
    // and drops the "Joy" step.
    features: ["music", "guestbook", "share"],
    coreFields: [],
    extraFields: [
      {
        key: "letter",
        label_uz: "Maktub (oydan maktub)",
        label_ru: "Письмо (письмо от луны)",
        label_en: "Letter (a letter from the moon)",
        type: "text_long",
        placeholder: "Tug'ilgan kuning muborak — bu yiling eng go'zal ertakdek o'tsin…",
      },
      {
        key: "closing_line",
        label_uz: "Yakuniy satr",
        label_ru: "Заключительная строка",
        label_en: "Closing line",
        type: "text_short",
        placeholder: "tug'ilgan kuning muborak. ertak davom etadi.",
      },
    ],
    extraLists: [
      {
        key: "chapters",
        label_uz: "To'rt bob (yillik tilaklar)",
        label_ru: "Четыре главы (пожелания на год)",
        label_en: "Four chapters (a year of wishes)",
        item: { title: "text_short", text: "text_long" },
        itemLabelsUz: { title: "Bob sarlavhasi", text: "Tilak matni" },
        min: 1,
        max: 6,
        // Pre-fill with the storybook's four default chapters (verbatim from
        // rumaysa.html) so the owner edits real copy, not a blank list.
        defaultRows: [
          { title: "A wish for wonder", text: "may you wander into rooms that feel like opening sentences." },
          { title: "A wish for laughter", text: "may your days carry a joy that never ends" },
          { title: "A wish for quiet", text: "may you find the kind of stillness where good ideas come to sit beside you." },
          { title: "A wish for love", text: "may you be celebrated, often and loudly, by every soft heart that orbits you." },
        ],
      },
    ],
  },

  // Round 12 — 2 designer watercolor wedding templates (lush garden
  // arch + parallax mountain range). Self-contained iframe-static
  // pages under /templates/garden-arch/ and /templates/mountain/.
  {
    id: "w32", title: "Bahor Bog'i", category: "wedding",
    layout: "garden-arch-preview", variant: "garden-arch-static",
    bg: "#FDF6EE", ink: "#3D3D35", accent: "#B89B5E",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w32.webp",
    names: "Quvonchbek & Alina", date: "05.07.2026", venue: "Bog' Saroyi",
    address: "Toshkent sh., Mirzo Ulug'bek tumani",
    time: "17:00",
    photos: [],
    features: ["countdown", "map", "rsvp", "share","music"],
    extraFields: [
      {
        key: "parents",
        label_uz: "Kuyov ota-onasi (yuqori qator)",
        label_ru: "Родители жениха (верхняя строка)",
        label_en: "Groom's parents (top line)",
        type: "text_short",
        placeholder: "Mrs. Ziyoda & Mr. Ali",
      },
      {
        key: "parents2",
        label_uz: "Kelin ota-onasi (ismlar ostida)",
        label_ru: "Родители невесты (под именами)",
        label_en: "Bride's parents (under the names)",
        type: "text_short",
        placeholder: "Mrs. Ziyoda & Mr. Ali",
      },
      {
        key: "footer_note",
        label_uz: "Yakuniy xabar",
        label_ru: "Заключительные слова",
        label_en: "Closing note",
        type: "text_long",
        placeholder: "Sevgi va minnatdorchilik bilan",
      },
      {
        key: "info_title",
        label_uz: "\"Bilib qo'ying\" sarlavhasi",
        label_ru: "Заголовок блока «Полезно знать»",
        label_en: "\"Good to Know\" title",
        type: "text_short",
        placeholder: "Bilib qo'ying",
      },
      {
        key: "info_text",
        label_uz: "\"Bilib qo'ying\" matni (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Текст «Полезно знать» (пусто — скрыть)",
        label_en: "\"Good to Know\" body (empty hides the section)",
        type: "text_long",
        placeholder: "Mehmonlar uchun foydali ma'lumot…",
      },
      {
        key: "default_lang",
        label_uz: "Standart til (mehmon birinchi marta ochganda)",
        label_ru: "Язык по умолчанию (при первом открытии)",
        label_en: "Default language (first visit)",
        type: "enum",
        options: ["uz", "en", "ru"],
      },
    ],
    extraLists: [
      {
        key: "schedule",
        label_uz: "Marosim jadvali",
        label_ru: "Расписание торжества",
        label_en: "Celebration timeline",
        item: { time: "time", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", title: "Voqea", description: "Tavsifi" },
        min: 0,
        max: 6,
      },
    ],
  },
  {
    id: "w33", title: "Tog' Sahari", category: "wedding",
    layout: "mountain-preview", variant: "mountain-static",
    bg: "#f1e3bf", ink: "#463a2c", accent: "#c0a05c",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w33.webp",
    names: "Aziza & Sardor", date: "12.09.2026", venue: "Chorbog' bog'i",
    address: "Toshkent sh., Chorbog' ko'chasi",
    time: "15:00",
    photos: [],
    // mountain.html reads names/date/time/venue/address/map_url + an
    // optional ?schedule= (owner-editable day timeline). When the owner
    // leaves the schedule empty the programme section is hidden — so
    // existing invitations that never set one simply don't show it.
    features: ["countdown", "map", "rsvp", "share","music"],
    extraFields: [
      {
        key: "ceremony_title",
        label_uz: "Marosim bo'limi — sarlavha (bo'sh — bo'lim yashiriladi)",
        label_ru: "Блок церемонии — заголовок (пусто — скрыть)",
        label_en: "Ceremony block — title (empty hides it)",
        type: "text_short",
        default: "Nikoh qasamyodi",
      },
      {
        key: "ceremony_text",
        label_uz: "Marosim bo'limi — matn",
        label_ru: "Блок церемонии — текст",
        label_en: "Ceremony block — text",
        type: "text_long",
        default: "Tog'lar bag'rida, eng yaqinlarimiz davrasida bir-birimizga bir umrlik so'z beramiz.",
      },
      {
        key: "ceremony_time",
        label_uz: "Marosim bo'limi — soat (bo'sh — asosiy vaqt)",
        label_ru: "Блок церемонии — время (пусто — основное время)",
        label_en: "Ceremony block — time (empty = main time)",
        type: "time",
        placeholder: "19:00",
      },
      {
        key: "celebration_title",
        label_uz: "Ziyofat bo'limi — sarlavha (bo'sh — bo'lim yashiriladi)",
        label_ru: "Блок банкета — заголовок (пусто — скрыть)",
        label_en: "Reception block — title (empty hides it)",
        type: "text_short",
        default: "Kechki bazm",
      },
      {
        key: "celebration_text",
        label_uz: "Ziyofat bo'limi — matn",
        label_ru: "Блок банкета — текст",
        label_en: "Reception block — text",
        type: "text_long",
        default: "Chiroqlar yog'dusida, kuy va kulgi bilan — birga raqsga tushamiz.",
      },
      {
        key: "celebration_time",
        label_uz: "Ziyofat bo'limi — soat (bo'sh — asosiy vaqt)",
        label_ru: "Блок банкета — время (пусто — основное время)",
        label_en: "Reception block — time (empty = main time)",
        type: "time",
        placeholder: "19:00",
      },
    ],
    extraLists: [
      {
        key: "schedule",
        label_uz: "Kun tartibi (bo'sh qoldirsangiz, ko'rinmaydi)",
        label_ru: "Программа дня (пусто — скрыть)",
        label_en: "Day's schedule (empty hides the section)",
        item: { time: "time", title: "text_short", description: "text_long" },
        itemLabelsUz: { time: "Vaqt", title: "Voqea", description: "Joy / tavsifi" },
        min: 0,
        max: 8,
      },
    ],
  },

  // Round 13 — celestial night-sky wedding (deep navy + gold, drifting
  // star field, moon dividers). Self-contained flat iframe-static page.
  {
    id: "w34", title: "Yulduzli Tun", category: "wedding",
    layout: "starry-night-preview", variant: "starry-night-static",
    bg: "#0A0F24", ink: "#E5D08C", accent: "#C8A84C",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w34.webp",
    names: "Jahongir & Kamila", date: "24.07.2026", venue: "To'yona Palace",
    address: "Toshkent sh., Yunusobod tumani",
    time: "10:00",
    photos: [],
    // starry-night.html reads names/date/time/venue/address + ?family=
    // ("Olimov oilasi" line under the names). Surface the family slot
    // through the editor via payload.custom.family — see the variant.
    features: ["countdown", "map", "rsvp", "share","music"],
    extraFields: [
      {
        key: "family",
        label_uz: "Oila nomi",
        label_ru: "Семья",
        label_en: "Family",
        type: "text_short",
        placeholder: "Olimov oilasi",
      },
    ],
  },

  // Round 14 — terracotta dried-floral wedding (terracotta + olive +
  // cream, floating cotton/wheat/lavender botanicals). Flat iframe-
  // static page at /templates/terracotta.html.
  {
    id: "w35", title: "Terrakota", category: "wedding",
    layout: "terracotta-preview", variant: "terracotta-static",
    bg: "#F5EDE0", ink: "#3A281C", accent: "#C1714F",
    font: "'Lora', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w35.webp",
    names: "Humoyun & Dilsora", date: "14.08.2026", venue: "Bek Palace",
    address: "Toshkent sh., Yashnobod tumani",
    time: "14:00",
    photos: [],
    // terracotta.html reads only names/date/time/venue/address.
    features: ["countdown", "map", "rsvp", "share","music"],
  },

  // Round 15 — paper-cut tunnel-book fairy-tale wedding (royal blue +
  // gold, 5-layer pointer/scroll parallax: sky, castle, garden, arch,
  // foreground; ambient moon + butterflies). Flat iframe-static page
  // at /templates/ertak.html.
  {
    id: "w36", title: "Ertak", category: "wedding",
    layout: "ertak-preview", variant: "ertak-static",
    bg: "#050d2a", ink: "#f6efd9", accent: "#c9a24a",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w36.webp",
    cardOverlay: "none", // castle illustration runs edge-to-edge
    names: "Anvar & Kamila", date: "12.12.2026", venue: "Bek To'yxona",
    address: "Toshkent sh., Mirobod tumani",
    time: "13:00",
    photos: [],
    // ertak.html reads only names/date/time/venue/address.
    features: ["countdown", "map", "rsvp", "share","music"],
  },

  // Round 16 — twilight fairy-tale tunnel-book wedding (deep purple-
  // twilight + dawn + pale gold + dusty rose + lavender, with arches,
  // balloons, butterflies, sparkles). Flat iframe-static page at
  // /templates/pari.html.
  {
    id: "w37", title: "Pari", category: "wedding",
    layout: "pari-preview", variant: "pari-static",
    bg: "#1a1530", ink: "#f8ecd9", accent: "#e8c995",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w37.webp",
    names: "Diyorbek & Omina", date: "15.09.2026", venue: "Bek Palace",
    address: "Namangan sh.",
    time: "13:00",
    photos: [],
    // pari.html reads only names/date/time/venue/address.
    features: ["countdown", "map", "rsvp", "share","music"],
  },

  // Round 17 — kids' circus / carnival birthday (aged parchment +
  // burgundy + gold; layered arches + floating balloons + bunting +
  // stars). Flat iframe-static page at /templates/sharlar.html.
  {
    id: "b11", title: "Sharlar", category: "birthday",
    layout: "sharlar-preview", variant: "sharlar-static",
    bg: "#e7d3a4", ink: "#4a0f1a", accent: "#c89b3c",
    font: "'Playfair Display', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/b11.webp",
    names: "Asilbek", date: "15.07.2026", venue: "Mahalla Restaurant",
    address: "Toshkent sh.",
    time: "17:30",
    photos: [],
    // sharlar.html reads names/date/time/venue/address/map_url + ?age
    // ("9 — turns nine —" line).
    features: ["countdown", "map", "rsvp", "share","music"],
    extraFields: [
      {
        key: "age",
        label_uz: "Yoshi",
        label_ru: "Возраст",
        label_en: "Age",
        type: "age",
        placeholder: "9",
      },
    ],
  },

  // Round 18 — sweet kids' garden-party birthday (sky-blue + sun-
  // yellow + cream + coral + mint; layered arches, floating balloons,
  // bees, butterflies, clouds). Flat iframe-static page at
  // /templates/asalari.html.
  {
    id: "b12", title: "Asalari", category: "birthday",
    layout: "asalari-preview", variant: "asalari-static",
    bg: "#fff7e6", ink: "#2c3a5e", accent: "#f0a93a",
    font: "'Fredoka', sans-serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/b12.webp",
    names: "Asila", date: "14.07.2026", venue: "Koşebaşı Restaurant",
    address: "Toshkent sh.",
    time: "08:00",
    photos: [],
    // asalari.html reads names/date/time/venue/address/map_url + ?age
    // (big numeral + "happy 3rd birthday").
    features: ["countdown", "map", "rsvp", "share","music"],
    extraFields: [
      {
        key: "age",
        label_uz: "Yoshi",
        label_ru: "Возраст",
        label_en: "Age",
        type: "age",
        placeholder: "3",
      },
    ],
  },

  // Round 10 — 2 new designer iframe-static templates (stormy nikoh +
  // GoT-themed wedding). Pinned at the absolute top so they surface
  // first on the gallery + landing's featured strip.
  {
    id: "w30", title: "Wedding is Coming", category: "wedding",
    layout: "wedding-is-coming-preview", variant: "wedding-is-coming-static",
    bg: "#0A0807", ink: "#E8E2D4", accent: "#C9A55F",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    names: "Sardor & Sevinch", date: "15.08.2026", venue: "Magnum Palace, Toshkent",
    address: "Toshkent sh., Yunusobod tumani",
    time: "18:00",
    photos: [],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },

  // Buklama — layered paper-diorama / tunnel-book wedding (was pinned
  // at the top of the catalog; un-pinned per product call so the
  // newer designer drops above show first).
  {
    id: "w31", title: "Buklama", category: "wedding",
    layout: "buklama-preview", variant: "buklama-static",
    bg: "#0d2a52", ink: "#F3E7C9", accent: "#C9A14C",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Aziza & Sardor", date: "12.06.2026", venue: "Magnum Palace",
    address: "Toshkent sh., Yunusobod tumani",
    time: "18:00",
    photos: [],
    features: ["countdown", "story", "schedule", "map", "rsvp", "share"],
  },
  {
    id: "n02", title: "Tun Nikohi", category: "nikoh",
    layout: "nikoh-preview", variant: "nikoh-static",
    bg: "#0A0E1A", ink: "#F0E8DC", accent: "#C9A55F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sardor & Sevinch", date: "15.08.2026", venue: "Magnum Palace, Toshkent",
    address: "Toshkent sh., Yunusobod tumani",
    time: "18:00",
    photos: [],
    features: ["countdown","schedule","map","share"],
  },

  // Round 9 — 25 bookie-style iframe-static templates (premium category).
  // These pin at the absolute top so the new Bookie category showcase
  // surfaces first on the gallery + landing's featured strip.
  {
    id: "bk01", title: "Oltin Sahifa", category: "bookie",
    layout: "bookie-paper-gold-preview", variant: "bookie-paper-gold-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#A7842F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Aziza & Sardor", date: "12.06.2026", venue: "Magnum Palace",
    address: "Yunusobod tumani, Toshkent",
    time: "18:00",
    photos: [],
    features: ["gallery","schedule","map","rsvp","music","dresscode"],
  },
  {
    id: "bk02", title: "Mehrli Bog'", category: "bookie",
    layout: "bookie-sage-preview", variant: "bookie-sage-static",
    bg: "#EEF0E8", ink: "#2B2E22", accent: "#7A8268",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Nilufar & Bekzod", date: "18.07.2026", venue: "Yashil Bog'",
    address: "Samarqand viloyati, Konstitutsiya ko'chasi 5",
    time: "17:00",
    photos: [],
    features: ["schedule","dresscode","gallery","map","rsvp","music"],
  },
  {
    id: "bk03", title: "Anor", category: "bookie",
    layout: "bookie-wine-preview", variant: "bookie-wine-static",
    bg: "#FAF3E3", ink: "#2A2118", accent: "#6B1F2A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Maftuna & Komron", date: "14.09.2026", venue: "Bek Saroyi",
    address: "Chilonzor tumani, Bunyodkor ko'chasi 12",
    time: "18:30",
    photos: [],
    features: ["timeline","story","rsvp"],
  },
  {
    id: "bk04", title: "Lojuvard", category: "bookie",
    layout: "bookie-teal-preview", variant: "bookie-teal-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#2C7873",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Lola & Sherzod", date: "25.08.2026", venue: "Marvarid Saroyi",
    address: "Buxoro shahri, Bahouddin Naqshbandi ko'chasi 14",
    time: "19:00",
    photos: [],
    features: ["gallery","map","dresscode","rsvp","music"],
  },
  {
    id: "bk05", title: "Tunqush", category: "bookie",
    layout: "bookie-midnight-preview", variant: "bookie-midnight-static",
    bg: "#1A1814", ink: "#F5EFE6", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sharq Texnologiya", date: "30.10.2026", venue: "Hyatt Regency",
    address: "Chilonzor tumani, Navoiy ko'chasi 1",
    time: "19:30",
    photos: [],
    features: ["agenda","speakers","dresscode","map","rsvp"],
  },
  {
    id: "bk06", title: "Mis Tafti", category: "bookie",
    layout: "bookie-copper-preview", variant: "bookie-copper-static",
    bg: "#FAF3E3", ink: "#2E2118", accent: "#C8804B",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Nodira · 35", date: "22.06.2026", venue: "Atlas Saroyi",
    address: "Yashnobod tumani, Bobur ko'chasi 18",
    time: "17:30",
    photos: [],
    features: ["gallery","story","rsvp","music"],
  },
  {
    id: "bk07", title: "Lavanda Tushi", category: "bookie",
    layout: "bookie-mauve-preview", variant: "bookie-mauve-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#6B4C8A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sevinch", date: "08.05.2026", venue: "Lavanda Saroyi",
    address: "Sergeli tumani, Yangiobod ko'chasi 7",
    time: "18:00",
    photos: [],
    features: ["wishlist","agenda","dresscode","rsvp"],
  },
  {
    id: "bk08", title: "Zumrad", category: "bookie",
    layout: "bookie-emerald-preview", variant: "bookie-emerald-static",
    bg: "#F6EFE0", ink: "#2A2A1A", accent: "#2C5F4F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Dilfuza & Akhror", date: "25.07.2026", venue: "Yashil Bog'",
    address: "Yunusobod tumani, Amir Temur shoh ko'chasi 100",
    time: "18:00",
    photos: [],
    features: ["schedule","gallery","map","rsvp","music"],
  },
  {
    id: "bk09", title: "Lola Yuzli", category: "bookie",
    layout: "bookie-blush-preview", variant: "bookie-blush-static",
    bg: "#F0D6CC", ink: "#2A2118", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Saodat & Nodir", date: "15.09.2026", venue: "Ulug'bek Saroyi",
    address: "Samarqand shahri, Registon ko'chasi 6",
    time: "18:30",
    photos: [],
    features: ["story","gallery","rsvp"],
  },
  {
    id: "bk10", title: "Dengiz Tubi", category: "bookie",
    layout: "bookie-navy-preview", variant: "bookie-navy-static",
    bg: "#FBF6EC", ink: "#1F2A4A", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/bk10.webp",
    names: "Sharq Texnologiya", date: "02.08.2026", venue: "Mirzo Ulug'bek Saroyi",
    address: "Mirzo Ulug'bek tumani, Buyuk Ipak Yo'li 8",
    time: "18:00",
    photos: [],
    // bookie-navy.html reads only names/date/venue/address/time. The
    // earlier list claimed timeline / story / agenda — none of which
    // the iframe renders.
    features: ["map","rsvp","music"],
  },
  {
    id: "bk11", title: "Quyosh Nuri", category: "bookie",
    layout: "bookie-amber-preview", variant: "bookie-amber-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#C8804B",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Iroda & Fazliddin", date: "22.06.2026", venue: "Aqua Park Saroyi",
    address: "Mirzo Ulug'bek tumani, Tinchlik ko'chasi 24",
    time: "17:00",
    photos: [],
    features: ["wishlist","gallery","rsvp"],
  },
  {
    id: "bk12", title: "Marvarid", category: "bookie",
    layout: "bookie-pearl-preview", variant: "bookie-pearl-static",
    bg: "#F4F4F0", ink: "#2A2826", accent: "#C4C6C0",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Kamila & Bekzod", date: "30.06.2026", venue: "Marvarid Saroyi",
    address: "Buxoro shahri, Lyabi-Hauz maydoni 3",
    time: "19:00",
    photos: [],
    features: ["gallery","story","rsvp","music","share"],
  },
  {
    id: "bk13", title: "Tuproq Guli", category: "bookie",
    layout: "bookie-terracotta-preview", variant: "bookie-terracotta-static",
    bg: "#FAF3E3", ink: "#2A2118", accent: "#B5523A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/bk13.webp",
    names: "Karimov oilasi", date: "14.08.2026", venue: "Yangi uy",
    address: "Yunusobod tumani, Magnum ko'chasi 9",
    time: "18:00",
    photos: [],
    features: ["map","dresscode","rsvp","music"],
  },
  {
    id: "bk14", title: "Toshbosma", category: "bookie",
    layout: "bookie-charcoal-preview", variant: "bookie-charcoal-static",
    bg: "#2A2826", ink: "#F5EFE6", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sharq Texnologiya", date: "11.10.2026", venue: "Bek Saroyi",
    address: "Chilonzor tumani, Bunyodkor 12",
    time: "19:00",
    photos: [],
    features: ["gallery","agenda","map","rsvp"],
  },
  {
    id: "bk15", title: "Atirgul", category: "bookie",
    layout: "bookie-rose-preview", variant: "bookie-rose-static",
    bg: "#FBF4ED", ink: "#2A2118", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Gulnoza & Davron", date: "18.05.2026", venue: "Chimgan Saroyi",
    address: "Bo'stonliq tumani, Chimgan ko'chasi 1",
    time: "17:30",
    photos: [],
    features: ["gallery","share","rsvp","music"],
  },
  {
    id: "bk16", title: "Zaytun", category: "bookie",
    layout: "bookie-olive-preview", variant: "bookie-olive-static",
    bg: "#FBF6EC", ink: "#2A2A1A", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sevara Karimova", date: "14.09.2026", venue: "TUIT",
    address: "Toshkent shahri, Mirzo Ulug'bek tumani",
    time: "18:00",
    photos: [],
    features: ["story","gallery","rsvp"],
  },
  {
    id: "bk17", title: "Vino Tushi", category: "bookie",
    layout: "bookie-burgundy-preview", variant: "bookie-burgundy-static",
    bg: "#FAF3E3", ink: "#2A2118", accent: "#5A1224",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Mohira & Jasur", date: "05.05.2026", venue: "Lochin Garden",
    address: "Samarqand shahri, Lochin bog'i 7",
    time: "18:30",
    photos: [],
    features: ["story","gallery","dresscode","rsvp","music"],
  },
  {
    id: "bk18", title: "Yashil Tosh", category: "bookie",
    layout: "bookie-jade-preview", variant: "bookie-jade-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#4B6F5A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Komila & Bekzod", date: "30.06.2026", venue: "Aral Garden",
    address: "Yunusobod tumani, Aral ko'chasi 5",
    time: "18:30",
    photos: [],
    features: ["schedule","story","gallery","rsvp"],
  },
  {
    id: "bk19", title: "Za'faron", category: "bookie",
    layout: "bookie-saffron-preview", variant: "bookie-saffron-static",
    bg: "#FAF3E3", ink: "#2A2118", accent: "#D88A2A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Karimov oilasi", date: "21.03.2026", venue: "Oilaviy bog'",
    address: "Samarqand shahri, Navro'z ko'chasi 21",
    time: "12:00",
    photos: [],
    features: ["story","gallery"],
  },
  {
    id: "bk20", title: "Qora Tosh", category: "bookie",
    layout: "bookie-onyx-preview", variant: "bookie-onyx-static",
    bg: "#14110D", ink: "#F5EFE6", accent: "#C9869B",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Aziza · 50 yosh", date: "12.11.2026", venue: "Hilton Tashkent",
    address: "Mirzo Ulug'bek tumani, Buyuk Ipak Yo'li 1",
    time: "19:30",
    photos: [],
    features: ["gallery","story","dresscode","rsvp","music"],
  },
  {
    id: "bk21", title: "Sham Sha'oma", category: "bookie",
    layout: "bookie-champagne-preview", variant: "bookie-champagne-static",
    bg: "#E8DBC1", ink: "#2A2118", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Madina & Tursun", date: "10.09.2026", venue: "Hyatt Regency",
    address: "Chilonzor tumani, Navoiy 1",
    time: "19:00",
    photos: [],
    features: ["story","gallery","agenda","rsvp","music"],
  },
  {
    id: "bk22", title: "O'rmon Bag'rida", category: "bookie",
    layout: "bookie-forest-preview", variant: "bookie-forest-static",
    bg: "#F6EFE0", ink: "#1F3B2E", accent: "#B89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Gulnora & Jamshid", date: "21.03.2026", venue: "Sumalak Saroyi",
    address: "Samarqand shahri, Navro'z ko'chasi 21",
    time: "18:00",
    photos: [],
    features: ["gallery","dresscode","schedule","map","rsvp","music"],
  },
  {
    id: "bk23", title: "Marjon", category: "bookie",
    layout: "bookie-coral-preview", variant: "bookie-coral-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#E66653",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Mohira · 28", date: "05.07.2026", venue: "Lochin Bog'",
    address: "Bo'stonliq tumani, Lochin bog'i 11",
    time: "17:00",
    photos: [],
    features: ["gallery","schedule","dresscode","rsvp","music"],
  },
  {
    id: "bk24", title: "Olxo'ri Guli", category: "bookie",
    layout: "bookie-plum-preview", variant: "bookie-plum-static",
    bg: "#FBF4EC", ink: "#2A2118", accent: "#6B2745",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Saodat & Nodir", date: "15.09.2026", venue: "Sitorai Mokhi-Khosa",
    address: "Buxoro shahri, Sitorai-Mahi-Hosa qasri 1",
    time: "18:00",
    photos: [],
    features: ["schedule","gallery","story","rsvp"],
  },
  {
    id: "bk25", title: "Osmon Ko'k", category: "bookie",
    layout: "bookie-azure-preview", variant: "bookie-azure-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#2C5C9F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Tech Summit Tashkent", date: "21.11.2026", venue: "Hyatt Regency",
    address: "Chilonzor tumani, Navoiy 1",
    time: "09:00",
    photos: [],
    features: ["speakers","agenda","map","dresscode","rsvp"],
  },

  // Round 8 — 4 designer iframe-static templates (Risograph birthday,
  // editorial corporate-anniversary "10 years", romantic one-question
  // ask, cinema-frame editorial wedding). These are the most recent
  // designer-built standalone HTML drops and sit at the absolute top of
  // the catalog so they show up first on the gallery + landing's
  // featured strip.
  {
    id: "w29", title: "Kinokadr", category: "wedding",
    layout: "wedding-editorial-cinema-preview", variant: "wedding-editorial-cinema-static",
    bg: "#0F0E0C", ink: "#EDE6D8", accent: "#C9A769",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Anvar & Madina", date: "28.09.2026", venue: "Magnum Palace, Toshkent",
    address: "Toshkent sh., Navoiy ko'chasi 1",
    time: "18:00",
    photos: [
      "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "y04", title: "Kvartal", category: "yubiley",
    layout: "corporate-quarterly-preview", variant: "corporate-quarterly-static",
    bg: "#F4F1EA", ink: "#0E0E0F", accent: "#B98A3F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sharq Texnologiya · 10", date: "12.07.2026", venue: "Hyatt Regency Tashkent",
    address: "Toshkent sh., Navoiy ko'chasi 1",
    time: "18:00",
    photos: [
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","timeline","schedule","map","rsvp","share"],
  },
  {
    id: "lk03", title: "Bir savol", category: "sevgi",
    layout: "marry-me-one-question-preview", variant: "marry-me-one-question-static",
    bg: "#1A0911", ink: "#F5E9D6", accent: "#D5A85A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Sherzod & Diyora", date: "14.02.2026", venue: "Toshkent",
    address: "Toshkent sh., Mustaqillik ko'chasi 12-uy",
    time: "19:30",
    photos: [
      "https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","map","share"],
  },
  {
    id: "b05", title: "Riso", category: "birthday",
    layout: "birthday-riso-preview", variant: "birthday-riso-static",
    bg: "#F2EBDC", ink: "#1A1A1F", accent: "#E75A2A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Malika · 30", date: "12.07.2026", venue: "Magnum Palace, Toshkent",
    address: "Toshkent sh., Bunyodkor ko'chasi 12-uy",
    time: "20:00",
    photos: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","wishlist","rsvp","share"],
  },

  // Round 7 — 5 mixed iframe-static templates (corporate yubiley,
  // romantic date-ask, soft-bloom birthday, two new wedding showcases).
  {
    id: "w27", title: "Alisher & Muazzam", category: "wedding",
    layout: "alisher-muazzam-preview", variant: "alisher-muazzam-static",
    bg: "#F5F1E8", ink: "#2D352B", accent: "#6B8459",
    font: "'Italiana', serif", price: 149000, badge: "New",
    names: "Alisher & Muazzam", date: "14.08.2026", venue: "Magnum Palace, Toshkent",
    photos: [
      "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "w28", title: "Anvar & Ma'suma", category: "wedding",
    layout: "anvar-masuma-preview", variant: "anvar-masuma-static",
    bg: "#14202F", ink: "#F2EADD", accent: "#C9A24A",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    names: "Anvar & Ma'suma", date: "28.09.2026", venue: "Magnum Palace, Toshkent",
    photos: [
      "https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "y03", title: "Yubiley · Korporativ", category: "yubiley",
    layout: "yubiley-korporativ-preview", variant: "yubiley-korporativ-static",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#B89968",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "Sharq Texnologiya · 10", date: "12.07.2026", venue: "Hyatt Regency Tashkent",
    address: "Toshkent sh., Navoiy ko'chasi 1",
    time: "18:00",
    photos: [
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","timeline","schedule","map","rsvp","share"],
  },
  {
    id: "lk02", title: "Uchrashuv", category: "sevgi",
    layout: "uchrashuv-preview", variant: "uchrashuv-static",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#6B1F2A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/lk02.webp",
    names: "Bobur & Aziza", date: "12.07.2026", venue: "Tenge Coffee, Toshkent",
    address: "Toshkent sh., Mustaqillik ko'chasi 12-uy",
    time: "19:30",
    photos: [
      "https://images.unsplash.com/photo-1521986670923-c309c1c11ec0?w=1200&q=80&auto=format&fit=crop",
    ],
    // uchrashuv.html reads names/recipient/date/time/venue/address +
    // ?phone. The variant already splits "names" on "&" → sender +
    // recipient, so no manifest entry needed for recipient; surface
    // phone explicitly.
    features: ["countdown","map","share","music"],
    extraFields: [
      {
        key: "phone",
        label_uz: "Telefon",
        label_ru: "Телефон",
        label_en: "Phone",
        type: "phone",
        placeholder: "+998 90 123 45 67",
      },
    ],
  },
  {
    id: "b04", title: "Barakali", category: "birthday",
    layout: "barakali-preview", variant: "barakali-static",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#2C7873",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/b04.webp",
    names: "Diyora", date: "12.07.2026", venue: "Magnum Palace, Toshkent",
    address: "Toshkent sh., Yunusobod tumani",
    time: "18:00",
    photos: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
    ],
    // barakali.html reads names/date/time/venue/address + ?age and
    // ?phone. The "music" param exists too but maps to a per-template
    // mp3 URL the editor doesn't currently expose — leaving that for
    // a future pass alongside the curated music library.
    features: ["countdown","rsvp","share","music"],
    extraFields: [
      {
        key: "age",
        label_uz: "Yoshi",
        label_ru: "Возраст",
        label_en: "Age",
        type: "age",
        placeholder: "5",
      },
      {
        key: "phone",
        label_uz: "Telefon",
        label_ru: "Телефон",
        label_en: "Phone",
        type: "phone",
        placeholder: "+998 90 123 45 67",
      },
    ],
  },

  {
    id: "w14", title: "Bookie", category: "wedding",
    layout: "diyora-preview", variant: "diyora-static",
    bg: "#FBF6EC", ink: "#2A2118", accent: "#A7842F",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    cardImage: "/templates/cards/w14.webp",
    names: "Madina & Bekzod", date: "12.06.2026", venue: "Magnum Palace, Toshkent",
    photos: [
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop",
    ],
    // diyora-shahzod.html reads names/date/time/venue/address (+ audio/
    // slug as internal plumbing). No story / gallery / schedule render
    // path in the iframe, so dropping those features stops the editor
    // from soliciting input it would discard.
    features: ["countdown","map","rsvp","share","music"],
  },
  {
    id: "w15", title: "Avgust", category: "wedding",
    layout: "avgust-preview", variant: "avgust-static",
    bg: "#F5F1E8", ink: "#2D352B", accent: "#B89B5E",
    font: "'Italiana', serif", price: 149000, badge: "New",
    names: "Madina & Eldor", date: "14.08.2026", venue: "Magnum Palace, Toshkent",
    photos: [
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "w16", title: "Sentabr", category: "wedding",
    layout: "sentabr-preview", variant: "sentabr-static",
    bg: "#14202F", ink: "#F2EADD", accent: "#C9A24A",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    names: "Nigora & Sherzod", date: "28.09.2026", venue: "Magnum Palace, Toshkent",
    photos: [
      "https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  // Round 6 — 10 mobile-first iframe-static wedding templates ────────
  {
    id: "w17", title: "Mehrli", category: "wedding",
    layout: "mehrli-preview", variant: "mehrli-static",
    bg: "#FDF1EA", ink: "#3A1F1A", accent: "#C9686C",
    font: "'Pinyon Script', cursive", price: 149000, badge: "New",
    names: "Sevinch & Otabek", date: "05.04.2026", venue: "Atlas Saroyi, Toshkent",
    photos: ["https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w18", title: "Tilakli", category: "wedding",
    layout: "tilakli-preview", variant: "tilakli-static",
    bg: "#EDEEDE", ink: "#2A2F1F", accent: "#6B8459",
    font: "'Italiana', serif", price: 149000, badge: "New",
    names: "Zilola & Akmal", date: "18.05.2026", venue: "Yashil Bog', Samarqand",
    photos: ["https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w19", title: "Olovgul", category: "wedding",
    layout: "olovgul-preview", variant: "olovgul-static",
    bg: "#F2E2C9", ink: "#3A1F12", accent: "#B65B3A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Munira & Rustam", date: "11.10.2026", venue: "Bek Saroyi, Toshkent",
    photos: ["https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w20", title: "Bezakli", category: "wedding",
    layout: "bezakli-preview", variant: "bezakli-static",
    bg: "#F5EDDD", ink: "#2A0E10", accent: "#6B1F2A",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "Dilfuza & Akhror", date: "25.07.2026", venue: "Hilton Tashkent",
    photos: ["https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w21", title: "Ipakli", category: "wedding",
    layout: "ipakli-preview", variant: "ipakli-static",
    bg: "#F0EBDC", ink: "#0F2A20", accent: "#1F6B4A",
    font: "'Italiana', serif", price: 149000, badge: "New",
    names: "Komila & Bekzod", date: "30.06.2026", venue: "Marvarid Saroyi, Buxoro",
    photos: ["https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w22", title: "Sokin", category: "wedding",
    layout: "sokin-preview", variant: "sokin-static",
    bg: "#FBFAF7", ink: "#0C0C0C", accent: "#0C0C0C",
    font: "'Italiana', serif", price: 149000, badge: "New",
    names: "Maftuna & Sherbek", date: "12.11.2026", venue: "International Hotel, Toshkent",
    photos: ["https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w23", title: "Mavjli", category: "wedding",
    layout: "mavjli-preview", variant: "mavjli-static",
    bg: "#EFF6F4", ink: "#0F2A3A", accent: "#1F4760",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Iroda & Fazliddin", date: "22.06.2026", venue: "Aqua Park Saroyi, Toshkent",
    photos: ["https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w24", title: "Lavanda", category: "wedding",
    layout: "lavanda-preview", variant: "lavanda-static",
    bg: "#F2EAF2", ink: "#2E2236", accent: "#8E7DB1",
    font: "'Pinyon Script', cursive", price: 149000, badge: "New",
    names: "Gulnoza & Davron", date: "08.05.2026", venue: "Lavanda Saroyi, Toshkent",
    photos: ["https://images.unsplash.com/photo-1525772764200-be829a350797?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w25", title: "Olcha", category: "wedding",
    layout: "olcha-preview", variant: "olcha-static",
    bg: "#F5EFE6", ink: "#2A0E14", accent: "#6B1F2A",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "Saodat & Nodir", date: "15.09.2026", venue: "Ulug'bek Saroyi, Samarqand",
    photos: ["https://images.unsplash.com/photo-1503416997304-7f8bf166c121?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "w26", title: "Quyoshli", category: "wedding",
    layout: "quyoshli-preview", variant: "quyoshli-static",
    bg: "#FFF4E6", ink: "#3A1A0E", accent: "#E0944A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Shahnoza & Aziz", date: "02.08.2026", venue: "Mirzo Ulug'bek Saroyi, Toshkent",
    photos: ["https://images.unsplash.com/photo-1543589923-a8a87d76f2bb?w=1200&q=80&auto=format&fit=crop"],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "bs02", title: "Beshik to'yi", category: "beshik",
    layout: "beshik-toyi-preview", variant: "beshik-toyi-static",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#C8804B",
    font: "'Fraunces', serif", price: 149000, badge: "New",
    names: "Aziza & Bobur", date: "12.07.2026", venue: "Bog' Bog'i Restorani, Toshkent",
    photos: ["https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=1200&q=80&auto=format&fit=crop"],
    features: ["story","wishlist","rsvp","share"],
  },
  {
    id: "w01", title: "Asal", category: "wedding", layout: "photo-bleed", variant: "editorial",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#6B1F2A",
    font: "'Playfair Display', serif", price: 149000, badge: "Bestseller",
    names: "Aziza & Sardor", date: "12.06.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w04", title: "Tilla", category: "wedding", layout: "noir-gold", variant: "magazine",
    bg: "#1A1814", ink: "#F5EFE6", accent: "#C9A24E",
    font: "'Italiana', serif", price: 149000, badge: "Premium",
    names: "Dilnoza & Akmal", date: "30.08.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","timeline","dresscode","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w03", title: "Bahor", category: "wedding", layout: "watercolor", variant: "photoled",
    bg: "#FAF4EC", ink: "#1F1D18", accent: "#8B5C7E",
    font: "'Allura', cursive", price: 149000, badge: "New",
    names: "Nilufar & Bekzod", date: "21.05.2026", venue: "Bukhara",
    photos: [
      "https://images.unsplash.com/photo-1525772764200-be829a350797?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "n01", title: "Nikoh Royal", category: "nikoh", layout: "arch-photo", variant: "ceremonial",
    bg: "#EFE5D5", ink: "#2A1F18", accent: "#6B1F2A",
    font: "'Marcellus', serif", price: 149000, badge: "Bestseller",
    names: "Zarina & Bobur", date: "18.05.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1509927083803-4bd519298ac4?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","quran","schedule","map","rsvp","share"],
  },
  {
    id: "w05", title: "Oqshom", category: "wedding", layout: "modernist", variant: "minimal",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#7A8268",
    font: "'Bodoni Moda', serif", price: 149000,
    names: "Sevara & Otabek", date: "14.09.2026", venue: "Khiva",
    photos: [
      "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","schedule","rsvp","share"],
  },
  {
    id: "b02", title: "Confetti", category: "birthday", layout: "playful", variant: "celebration",
    bg: "#FFF5E8", ink: "#2A1F18", accent: "#C8804B",
    font: "'Yeseva One', serif", price: 149000, badge: "New",
    names: "Diyor", date: "27.06.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","gallery","wishlist","rsvp","share"],
  },
  {
    id: "bs01", title: "Beshik", category: "beshik", layout: "soft-pastel", variant: "newborn",
    bg: "#F0E8DC", ink: "#2A2520", accent: "#7A8268",
    font: "'Lora', serif", price: 149000, badge: "Bestseller",
    names: "Amir", date: "05.06.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519689680058-324335c77eba?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1492725764893-90b379c2b6e7?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["story","gallery","rsvp","share"],
  },
  {
    id: "c01", title: "Forum", category: "corporate", layout: "corporate-grid", variant: "agenda",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#1A1814",
    font: "'DM Mono', monospace", price: 149000, badge: "Premium",
    names: "ANNUAL & GALA", date: "15.10.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["agenda","speakers","map","rsvp","share"],
  },
  {
    id: "m01", title: "Tinch", category: "memorial", layout: "memorial-frame", variant: "memorial",
    bg: "#EFEAE2", ink: "#2A2520", accent: "#3A352E",
    font: "'Marcellus', serif", price: 149000,
    names: "Karim Yusupov", date: "08.05.2026", venue: "Tashkent",
    // Memorial defaults render an inline SVG candle (see memorial.tsx) so the
    // page never accidentally surfaces a generic stock-photo subject.
    photos: [],
    features: ["bio","map","share"],
  },

  // ── 10 NEW templates ──
  {
    id: "w06", title: "Sumalak", category: "wedding", layout: "miniature", variant: "botanical",
    bg: "#F2EDE0", ink: "#1F2A22", accent: "#2D5042",
    font: "'Cinzel', serif", price: 149000, badge: "New",
    names: "Gulnora & Jamshid", date: "21.03.2026", venue: "Samarkand",
    photos: [
      "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w07", title: "Marvarid", category: "wedding", layout: "coastal", variant: "seaside",
    bg: "#F0E4D0", ink: "#2A3A3A", accent: "#5C8A8A",
    font: "'Tenor Sans', sans-serif", price: 149000, badge: "New",
    names: "Lola & Jasur", date: "18.07.2026", venue: "Charvak",
    photos: [
      "https://images.unsplash.com/photo-1535924206242-349a96155006?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "y01", title: "Yubiley", category: "yubiley", layout: "milestone", variant: "jubilee",
    bg: "#E0DCD2", ink: "#1B1F2A", accent: "#B0AAA0",
    font: "'Cormorant Garamond', serif", price: 149000,
    names: "Madina & Tursun · 25", date: "10.09.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","timeline","schedule","rsvp","guestbook","music","share"],
  },
  {
    id: "f01", title: "Fotixa", category: "fotixa", layout: "rose-gold", variant: "engagement",
    bg: "#F8F1E8", ink: "#3A2620", accent: "#D4A5A5",
    font: "'Pinyon Script', cursive", price: 149000, badge: "New",
    names: "Aziza & Sardor", date: "05.04.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1525258946800-98cfd641d0de?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  {
    id: "sn01", title: "Sunnat", category: "sunnat", layout: "suzani-burst", variant: "celebration-sunnat",
    bg: "#FFF8E6", ink: "#2A1F18", accent: "#C8804B",
    font: "'Yeseva One', serif", price: 149000, badge: "Bestseller",
    names: "Behruz", date: "12.07.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1492725764893-90b379c2b6e7?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "g01", title: "Bitiruv", category: "graduation", layout: "scroll-academic", variant: "academia",
    bg: "#F5EFE6", ink: "#1A2A50", accent: "#A02230",
    font: "'Cormorant SC', serif", price: 149000, badge: "New",
    names: "Sevinch Karimova", date: "25.06.2026", venue: "TUIT, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","rsvp","share"],
  },
  {
    id: "b03", title: "Bayramcha", category: "birthday", layout: "champagne", variant: "adult-birthday",
    bg: "#F5E8D8", ink: "#2A2520", accent: "#C89F8A",
    font: "'Bodoni Moda', serif", price: 149000,
    names: "Nodira", date: "14.08.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1481018085669-2bc6e4f00eed?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","rsvp","music","share"],
  },
  {
    id: "c02", title: "Studio", category: "corporate", layout: "neon-tech", variant: "launch-event",
    bg: "#0A0A0A", ink: "#F0F0F0", accent: "#00FF88",
    font: "'JetBrains Mono', monospace", price: 149000, badge: "Premium",
    names: "PRODUCT × LAUNCH", date: "22.11.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","agenda","speakers","map","rsvp","share"],
  },
  {
    id: "m02", title: "Yodgorlik", category: "memorial", layout: "geometric-mosaic", variant: "reverent",
    bg: "#EFE9DD", ink: "#2A2520", accent: "#B89968",
    font: "'Marcellus', serif", price: 149000,
    names: "Olim Yusupov", date: "15.06.2026", venue: "Bukhara",
    // Memorial defaults render an inline SVG candle (see reverent.tsx) so the
    // page never accidentally surfaces a generic stock-photo subject.
    photos: [],
    features: ["bio","quran","map","share"],
  },
  {
    id: "r01", title: "Mehmonxona", category: "corporate", layout: "hospitality", variant: "restaurant-opening",
    bg: "#F5EFE6", ink: "#3A2D1F", accent: "#B89968",
    font: "'Marcellus', serif", price: 149000, badge: "New",
    names: "PALOV & CO", date: "08.10.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },

  // ── 10 NEW templates (round 3) — 5 corporate-focused + 5 unique ──
  {
    id: "c03", title: "Boshlanish", category: "corporate", layout: "corp-anniversary", variant: "corp-anniversary",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#6B1F2A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "ACME · 10 YEARS", date: "12.11.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","timeline","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "c04", title: "Bozor", category: "corporate", layout: "retail-opening", variant: "retail-opening",
    bg: "#FFF5E8", ink: "#2A1F18", accent: "#C8804B",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "ATLAS BOUTIQUE", date: "20.09.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "c05", title: "Konferentsiya", category: "corporate", layout: "conf-tracks", variant: "conf-tracks",
    bg: "#F5EFE6", ink: "#1A1814", accent: "#1A1814",
    font: "'Inter', sans-serif", price: 149000, badge: "Premium",
    names: "TECH SUMMIT 2026", date: "10.10.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","agenda","speakers","map","rsvp","share"],
  },
  {
    id: "c06", title: "Murabbiy", category: "corporate", layout: "masterclass", variant: "masterclass",
    bg: "#EFEAE2", ink: "#2A2520", accent: "#3A352E",
    font: "'Marcellus', serif", price: 149000, badge: "New",
    names: "MASTERCLASS · ART OF UI", date: "18.07.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","schedule","speakers","map","rsvp","share"],
  },
  {
    id: "c07", title: "Xayriya", category: "corporate", layout: "charity-elegant", variant: "charity-elegant",
    bg: "#F8F2E8", ink: "#1F1815", accent: "#8B6F47",
    font: "'Italiana', serif", price: 149000, badge: "Premium",
    names: "MEHR-O-MUHABBAT GALA", date: "05.12.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1578357078586-491adf1aa5ba?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },

  // ── 5 unique non-corporate ──
  {
    id: "u01", title: "Yangi uy", category: "uy", layout: "housewarming", variant: "housewarming",
    bg: "#F5EAD8", ink: "#2A2520", accent: "#A2652A",
    font: "'Lora', serif", price: 149000, badge: "New",
    names: "Karimovlar oilasi", date: "08.06.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1513694203232-719a280e022f?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","share"],
  },
  {
    id: "w08", title: "Sahar", category: "wedding", layout: "sunrise", variant: "sunrise-wedding",
    bg: "#F8E8D8", ink: "#2A1A14", accent: "#D89968",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Madina & Eldor", date: "18.05.2026", venue: "Chimgan",
    photos: [
      "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "y02", title: "Yubiley 50", category: "yubiley", layout: "milestone-birthday", variant: "milestone-birthday",
    bg: "#F5E8DC", ink: "#1F1A14", accent: "#A02230",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "Bekzod aka · 50 yosh", date: "22.08.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1481018085669-2bc6e4f00eed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","rsvp","music","share"],
  },
  {
    id: "lk01", title: "Sevgi kuni", category: "sevgi", layout: "lovers", variant: "lovers",
    bg: "#F8E8E8", ink: "#3A1F22", accent: "#B0405A",
    font: "'Pinyon Script', cursive", price: 149000, badge: "New",
    names: "Aziza & Sardor · 1 yil", date: "14.02.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1459501462159-97d5bded1416?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","rsvp","share"],
  },
  {
    id: "i01", title: "Iftor", category: "iftor", layout: "iftar", variant: "iftar",
    bg: "#F2E5C6", ink: "#2A2014", accent: "#7B5430",
    font: "'Marcellus', serif", price: 149000, badge: "New",
    names: "Karimovlar iftori", date: "20.03.2026", venue: "Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1591343395082-e120087004b4?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","schedule","map","rsvp","share"],
  },
  // ── Round 4 — 5 maximalist wedding templates ─────────────────────────
  {
    id: "w09", title: "Saodat", category: "wedding", layout: "baroque-gold", variant: "baroque-gold",
    bg: "#F4E9D2", ink: "#1A1208", accent: "#A8862A",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "Premium",
    names: "Sevinch & Otabek", date: "12.09.2026", venue: "Magnum Palace, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1537633552985-df8429e8048b?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w10", title: "Lola", category: "wedding", layout: "tulip-miniature", variant: "tulip-miniature",
    bg: "#FBE9D8", ink: "#3A1414", accent: "#C73E2E",
    font: "'Cormorant Garamond', serif", price: 149000, badge: "New",
    names: "Mohira & Jasur", date: "05.05.2026", venue: "Lochin Garden, Samarqand",
    photos: [
      "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1525772764200-be829a350797?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1503416997304-7f8bf166c121?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w11", title: "Yulduz", category: "wedding", layout: "celestial-foil", variant: "celestial-foil",
    bg: "#0E1430", ink: "#F5EFE6", accent: "#D4B886",
    font: "'Bodoni Moda', serif", price: 149000, badge: "Premium",
    names: "Dilnoza & Bekzod", date: "21.11.2026", venue: "Hyatt Regency, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w12", title: "Gulshan", category: "wedding", layout: "garden-cascade", variant: "garden-cascade",
    bg: "#F5F0E2", ink: "#2A2A1F", accent: "#8C5A3C",
    font: "'Playfair Display', serif", price: 149000, badge: "Bestseller",
    names: "Nigora & Sherzod", date: "18.06.2026", venue: "Aral Garden, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1543589923-a8a87d76f2bb?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1533632359083-0185df1be85d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "w13", title: "Qadr", category: "wedding", layout: "bukhara-royal", variant: "bukhara-royal",
    bg: "#1A0E18", ink: "#F4E5C2", accent: "#E2A630",
    font: "'Bodoni Moda', serif", price: 149000, badge: "Premium",
    names: "Shahnoza & Komron", date: "30.10.2026", venue: "Sitorai Mokhi-Khosa, Buxoro",
    photos: [
      "https://images.unsplash.com/photo-1602446347559-dc2d3c2b3f3c?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  // ── Round 5 — 5 maximalist birthday templates ────────────────────────
  {
    id: "b09", title: "Karnaval", category: "birthday", layout: "kid-carnival", variant: "kid-carnival",
    bg: "#FFF1E0", ink: "#1F2330", accent: "#E84A5F",
    font: "'Yeseva One', serif", price: 149000, badge: "Bestseller",
    names: "Mironbek 7 yosh", date: "08.07.2026", venue: "Magic City, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","music","share"],
  },
  {
    id: "b10", title: "Malika", category: "birthday", layout: "princess-castle", variant: "princess-castle",
    bg: "#FFE9F0", ink: "#3A1F2E", accent: "#D8B14C",
    font: "'Pinyon Script', cursive", price: 149000, badge: "Premium",
    names: "Sevara malika 5 yosh", date: "20.04.2026", venue: "Hyatt Ballroom, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1546015720-b8b30df5aa27?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","music","share"],
  },
  {
    id: "b06", title: "Olov", category: "birthday", layout: "sparkler-milestone", variant: "sparkler-milestone",
    bg: "#0E0B14", ink: "#F5EFE6", accent: "#E2A630",
    font: "'Bodoni Moda', serif", price: 149000, badge: "New",
    names: "Sherzod 30 yosh", date: "14.08.2026", venue: "International Hotel, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1535827841776-24afc1e255ac?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","guestbook","music","share"],
  },
  {
    id: "b07", title: "Tomosha", category: "birthday", layout: "vintage-fair", variant: "vintage-fair",
    bg: "#F4E8D0", ink: "#2A1410", accent: "#C73E2E",
    font: "'Playfair Display', serif", price: 149000, badge: "New",
    names: "Aziza 25 yosh", date: "10.10.2026", venue: "Yangiobod Park, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1535827841776-24afc1e255ac?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","music","share"],
  },
  {
    id: "b08", title: "Disko", category: "birthday", layout: "disco-neon", variant: "disco-neon",
    bg: "#0A0712", ink: "#F5EFE6", accent: "#FF2D7A",
    font: "'Bodoni Moda', serif", price: 149000, badge: "Premium",
    names: "Jasur 21 yosh", date: "29.05.2026", venue: "NEON Club, Tashkent",
    photos: [
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1571266028243-d220c6a1d2ab?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1200&q=80&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1574391884720-bbc049ec09ad?w=1200&q=80&auto=format&fit=crop",
    ],
    features: ["countdown","story","gallery","schedule","map","rsvp","music","share"],
  },
];

// ── International-mode demo names (taklifla.com) ────────────────────────
// On the international host (see market.ts's isIntlSite()), catalog/demo/
// preview surfaces — gallery cards, the /i/<id> demo preview, and the
// create-form's prefilled default — show English couple names instead of
// the Uzbek/Russian ones baked into TEMPLATES above. Titles, venues, and
// addresses are ALSO localized to English/international via EN_CATALOG below
// (prices convert to USD display-only in formatPrice). This is catalog/demo
// content ONLY, and it can never leak into a real invitation, because owner
// names live in the invitation's payload, not in this static catalog.
//
// w27 and w28 are the two exceptions: their demo names ARE the template's
// product identity ("Alisher & Muazzam" / "Anvar & Ma'suma" are literally
// what those templates are called), so they keep their original names on
// every host.
const EN_DEMO_NAME_EXEMPT_IDS: ReadonlySet<string> = new Set(["w27", "w28"]);

// A small pool of tasteful English couple names. Any template id not given
// an explicit entry in EN_DEMO_NAMES below falls back to a stable pick from
// this pool (see stableIndex), so the same id always renders the same pair
// across sessions/builds instead of flickering between page loads.
const EN_DEMO_NAME_POOL: readonly string[] = [
  "William & Emma",
  "Oliver & Sophia",
  "James & Amelia",
  "Henry & Charlotte",
  "Alexander & Isabella",
  "Benjamin & Grace",
  "Thomas & Victoria",
  "Edward & Eleanor",
  "Nathaniel & Olivia",
  "Sebastian & Claire",
  "Theodore & Margaret",
  "Frederick & Josephine",
];

// Single English names for occasions that aren't a two-person couple —
// birthdays, beshik-to'yi, sunnat, graduation. Using a couple pair there
// ("William & Emma" on a child's birthday card) reads wrong, so those
// categories get one name instead.
const EN_DEMO_SINGLE_POOL: readonly string[] = [
  "Emma", "Oliver", "Sophia", "Liam", "Charlotte",
  "Noah", "Amelia", "Lucas", "Grace", "Ethan",
];

// Categories whose demo name should be a COUPLE pair. Everything else
// (birthday/beshik/sunnat/etc.) uses a single name; a handful of impersonal
// categories (corporate) are left untouched entirely — see below.
const COUPLE_CATEGORIES: ReadonlySet<string> = new Set([
  "wedding", "nikoh", "sevgi",
]);
// Categories where the demo "name" isn't a person at all (company/event
// names) — leave them exactly as authored on every host.
const IMPERSONAL_CATEGORIES: ReadonlySet<string> = new Set([
  "corporate", "yubiley",
]);

// Explicit id → name overrides, for the rare id where the pool's stable
// pick should be pinned rather than hashed. Empty today; kept so a future
// one-off fix doesn't need to touch the hashing logic.
const EN_DEMO_NAMES: Readonly<Record<string, string>> = {};

/** Stable (deterministic) string hash — same template id always picks the
 *  same pool entry, on every build and every render. Not cryptographic,
 *  just needs to spread ids evenly across the pool. */
function stableIndex(id: string, mod: number): number {
  let h = 5381;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) + h + id.charCodeAt(i)) | 0; // djb2
  }
  return Math.abs(h) % mod;
}

function englishDemoNames(id: string, category: string): string | null {
  if (EN_DEMO_NAMES[id]) return EN_DEMO_NAMES[id];
  if (IMPERSONAL_CATEGORIES.has(category)) return null; // leave as authored
  const pool = COUPLE_CATEGORIES.has(category) ? EN_DEMO_NAME_POOL : EN_DEMO_SINGLE_POOL;
  return pool[stableIndex(id, pool.length)];
}

// ── International-mode catalog localization (taklifla.com) ──────────────
// English template titles + city-neutral international venues/addresses for
// the .com international audience. Generated for all 109 templates (see the
// taklifla-com-localize workflow). DEMO/CATALOG content ONLY — applied to the
// static TEMPLATES array at import time on the intl host; it can never reach a
// real invitation, whose owner data lives in the invitation payload.
const EN_CATALOG: Readonly<
  Record<string, { title?: string; venue?: string; address?: string }>
> = {
  w47: { title: "The Envelope", venue: "Candlelight Hall", address: "12 Wexford Lane" },
  w46: { title: "Four Seasons", venue: "The Grand Conservatory", address: "24 Rosewood Avenue" },
  w45: { title: "Endless Sky", venue: "Celeste Pavilion", address: "8 Skyline Terrace" },
  w44: { title: "Golden Route", venue: "The Gilded Hall", address: "16 Ashcombe Road" },
  w43: { title: "Paper Story", venue: "Ashford Manor", address: "3 Parchment Lane" },
  w42: { title: "Sakura", venue: "Cherry Blossom Pavilion", address: "11 Blossom Court" },
  w41: { title: "Meadow", venue: "Willow Meadow Hall", address: "27 Meadowbrook Way" },
  w40: { title: "After the Rain", venue: "The Rainwood Pavilion", address: "5 Amberdale Street" },
  w39: { title: "Midnight Palace", venue: "Nocturne Palace", address: "42 Crescent Row" },
  n03: { title: "Peacock", venue: "Plumage Hall", address: "9 Highgate Lane" },
  w38: { title: "Marigold", venue: "Marigold Court", address: "18 Larkfield Avenue" },
  b13: { title: "Four Wishes", venue: "The Night Library", address: "7 Lantern Close" },
  w32: { title: "Spring Garden", venue: "The Garden Pavilion", address: "18 Bloomsbury Lane" },
  w33: { title: "Mountain Dawn", venue: "The Highland Retreat", address: "7 Summit Ridge Road" },
  w34: { title: "Starlit Night", venue: "The Celestial Ballroom", address: "42 Observatory Way" },
  w35: { title: "Terracotta", venue: "Rosewood Manor", address: "9 Clay Court" },
  w36: { title: "Storybook", venue: "The Enchanted Hall", address: "3 Fable Street" },
  w37: { title: "Fae", venue: "Ashford Hall", address: "21 Willowmere Avenue" },
  b11: { title: "Balloons", venue: "The Sunbeam Loft", address: "5 Carnival Close" },
  b12: { title: "Little Bee", venue: "Honeycomb House", address: "14 Meadow Row" },
  w30: { title: "Wedding is Coming", venue: "Winterfell Grand Hall", address: "1 Northgate Terrace" },
  w31: { title: "Folded", venue: "The Paper Pavilion", address: "26 Origami Lane" },
  n02: { title: "Midnight Vows", venue: "The Moonlight Ballroom", address: "33 Evenstar Boulevard" },
  bk01: { title: "Golden Page", venue: "The Illuminated Hall", address: "5 Kingsway Court" },
  bk02: { title: "Tender Garden", venue: "Camellia Gardens", address: "12 Camellia Lane" },
  bk03: { title: "Pomegranate", venue: "Garnet Hall", address: "16 Orchard Row" },
  bk04: { title: "Lapis", venue: "Azure Pavilion", address: "14 Bridgewater Road" },
  bk05: { title: "Night Owl", venue: "Hyatt Regency", address: "1 Riverside Drive" },
  bk06: { title: "Copper Glow", venue: "The Foundry Conservatory", address: "18 Foundry Street" },
  bk07: { title: "Lavender Dream", venue: "Lavender Estate", address: "7 Lavender Way" },
  bk08: { title: "Emerald", venue: "Emerald Court", address: "100 Windsor Boulevard" },
  bk09: { title: "Tulip", venue: "Tulip Hall", address: "6 Blossom Street" },
  bk10: { title: "Ocean Deep", venue: "The Coral Pavilion", address: "8 Marine Parade" },
  bk11: { title: "Sunlight", venue: "Sunlit Terrace", address: "24 Sunnyside Road" },
  bk12: { title: "Pearl", venue: "Marble Court", address: "3 Lakeside Square" },
  bk13: { title: "Clay Blossom", venue: "The Garden Terrace", address: "9 Magnolia Court" },
  bk14: { title: "Lithograph", venue: "The Printworks Gallery", address: "12 Ashford Lane" },
  bk15: { title: "Rose", venue: "The Rose Conservatory", address: "1 Highland Road" },
  bk16: { title: "Olive", venue: "Olive Grove Estate", address: "18 Wellesley Street" },
  bk17: { title: "Wine Dream", venue: "Vinemount Estate", address: "7 Vineyard Row" },
  bk18: { title: "Jade Stone", venue: "Willowmere Gardens", address: "5 Willow Drive" },
  bk19: { title: "Saffron", venue: "The Orchard Pavilion", address: "21 Meadow Lane" },
  bk20: { title: "Obsidian", venue: "Hilton", address: "1 Kensington High Street, London" },
  bk21: { title: "Candlelight", venue: "The Candlelight Rooms", address: "1 Navarre Boulevard" },
  bk22: { title: "Forest Embrace", venue: "The Woodland Hall", address: "21 Forest Glade Road" },
  bk23: { title: "Coral", venue: "Marina Gardens", address: "14 Harbour View" },
  bk24: { title: "Plum Blossom", venue: "The Moonwater Palace", address: "1 Orchard Court" },
  bk25: { title: "Azure Sky", venue: "The Cerulean Hall", address: "1 Skyline Avenue" },
  w29: { title: "Cinéma", venue: "The Palladium", address: "9 Marquee Street" },
  y04: { title: "The Quarter", venue: "Hyatt Regency", address: "1 Meridian Boulevard" },
  lk03: { title: "One Question", venue: "The Willow Terrace", address: "12 Independence Walk" },
  b05: { title: "Riso", venue: "Bramble House", address: "12 Builders Way" },
  w27: { title: "Alisher & Muazzam", venue: "Rosecliff Manor", address: "1 Rosewood Avenue" },
  w28: { title: "Anvar & Ma'suma", venue: "Laurel Manor", address: "1 Laurel Crescent" },
  y03: { title: "Anniversary · Corporate", venue: "Aurora Grand Ballroom", address: "40 Corporate Drive" },
  lk02: { title: "Rendezvous", venue: "The Rendezvous Café", address: "12 Highgate Lane" },
  b04: { title: "Blessed", venue: "Grace Hall", address: "18 Maple Court" },
  w14: { title: "Bookie", venue: "Whitmore Manor" },
  w15: { title: "August", venue: "Summerfield House" },
  w16: { title: "September", venue: "Autumn Grove Manor" },
  w17: { title: "Tender", venue: "Fernwood Gardens" },
  w18: { title: "Wishes", venue: "The Verdant Garden" },
  w19: { title: "Flame Blossom", venue: "Crimson Manor" },
  w20: { title: "Ornate", venue: "Belvedere Palace" },
  w21: { title: "Silk", venue: "The Silk Pavilion" },
  w22: { title: "Serene", venue: "International Hotel" },
  w23: { title: "Waves", venue: "The Riverside Pavilion" },
  w24: { title: "Lavender", venue: "Provence Manor" },
  w25: { title: "Cherry", venue: "Cherrywood Hall" },
  w26: { title: "Sunlit", venue: "Goldenhour Estate" },
  bs02: { title: "Little One", venue: "The Garden Table" },
  w01: { title: "Honey", venue: "Amberley House" },
  w04: { title: "Golden", venue: "The Gilded Ballroom" },
  w03: { title: "Spring", venue: "Primrose Pavilion" },
  n01: { title: "Royal Vows", venue: "Kensington Hall" },
  w05: { title: "Dusk", venue: "Nightingale Hall" },
  b02: { title: "Confetti", venue: "The Festival Room" },
  bs01: { title: "Cradle", venue: "Rosevale Cottage" },
  c01: { title: "Forum", venue: "The Summit Center" },
  m01: { title: "Serenity", venue: "Rosemead Chapel" },
  w06: { title: "Springtide", venue: "Verdemont Manor" },
  w07: { title: "Pearl", venue: "The Pearl Pavilion" },
  y01: { title: "Jubilee", venue: "The Jubilee Hall" },
  f01: { title: "Betrothal", venue: "Magnolia Manor" },
  sn01: { title: "Sunnat", venue: "Cedarhurst Hall" },
  g01: { title: "Commencement", venue: "The Grand Auditorium" },
  b03: { title: "Festive", venue: "Bluebell Hall" },
  c02: { title: "Studio", venue: "The Loft Studio" },
  m02: { title: "Remembrance", venue: "Ashgrove Memorial Chapel" },
  r01: { title: "The Grand Opening", venue: "The Metropolitan Rooms" },
  c03: { title: "Cornerstone", venue: "Kingsley Conference Center" },
  c04: { title: "The Marketplace", venue: "The Exchange Hall" },
  c05: { title: "The Summit", venue: "Whitfield Convention Center" },
  c06: { title: "Masterclass", venue: "The Bradbury Studio" },
  c07: { title: "The Benefit Gala", venue: "The Astor Ballroom" },
  u01: { title: "Housewarming", venue: "Hearthstone House" },
  w08: { title: "First Light", venue: "Dawnridge Estate" },
  y02: { title: "Golden Jubilee", venue: "Windsor Grand Hall" },
  lk01: { title: "Be Mine", venue: "Rosepetal Terrace" },
  i01: { title: "Iftar", venue: "The Crescent Pavilion" },
  w09: { title: "Gilded Bliss", venue: "Goldleaf Palace" },
  w10: { title: "Tulip", venue: "Petal Court" },
  w11: { title: "Celestial", venue: "Astral Hall" },
  w12: { title: "Rose Garden", venue: "The Rosewalk Gardens" },
  w13: { title: "Royal Ornament", venue: "The Regal Palace" },
  b09: { title: "Carnival", venue: "Wonderland Hall" },
  b10: { title: "Little Princess", venue: "The Crystal Ballroom" },
  b06: { title: "Sparkler", venue: "Celebration Hall" },
  b07: { title: "Vintage Fair", venue: "The Fairground Green" },
  b08: { title: "Disco Nights", venue: "Neon Lounge" },
};

if (isIntlSite()) {
  for (const tpl of TEMPLATES) {
    // English title + international venue/address for the .com catalog.
    const loc = EN_CATALOG[tpl.id];
    if (loc) {
      if (loc.title) tpl.title = loc.title;
      if (loc.venue) tpl.venue = loc.venue;
      if (loc.address) tpl.address = loc.address;
    }
    // English demo couple/single name (unchanged behaviour).
    if (EN_DEMO_NAME_EXEMPT_IDS.has(tpl.id)) continue;
    const en = englishDemoNames(tpl.id, tpl.category);
    if (en) tpl.names = en;
  }
}

export interface Category {
  id: TemplateCategory | "all";
  /**
   * i18n key for the category label. Resolve via t(labelKey) at render time.
   * Keys live in i18n.uz/ru/en under the `category.*` namespace.
   */
  labelKey: string;
  count: number;
}

// Counts derive from TEMPLATES so the chip badges, the gallery header count,
// and the actual rendered tile count never drift from each other (QA round-2
// flagged a 58/53/57 three-way mismatch).
const _categoryCount = (id: string): number =>
  id === "all"
    ? TEMPLATES.length
    : TEMPLATES.filter((t) => t.category === id).length;

export const CATEGORIES: Category[] = [
  { id: "all",        labelKey: "category.all",        count: _categoryCount("all") },
  { id: "bookie",     labelKey: "category.bookie",     count: _categoryCount("bookie") },
  { id: "wedding",    labelKey: "category.wedding",    count: _categoryCount("wedding") },
  { id: "nikoh",      labelKey: "category.nikoh",      count: _categoryCount("nikoh") },
  { id: "fotixa",     labelKey: "category.fotixa",     count: _categoryCount("fotixa") },
  { id: "sevgi",      labelKey: "category.sevgi",      count: _categoryCount("sevgi") },
  { id: "birthday",   labelKey: "category.birthday",   count: _categoryCount("birthday") },
  { id: "beshik",     labelKey: "category.beshik",     count: _categoryCount("beshik") },
  { id: "sunnat",     labelKey: "category.sunnat",     count: _categoryCount("sunnat") },
  { id: "yubiley",    labelKey: "category.yubiley",    count: _categoryCount("yubiley") },
  { id: "graduation", labelKey: "category.graduation", count: _categoryCount("graduation") },
  { id: "corporate",  labelKey: "category.corporate",  count: _categoryCount("corporate") },
  { id: "uy",         labelKey: "category.uy",         count: _categoryCount("uy") },
  { id: "iftor",      labelKey: "category.iftor",      count: _categoryCount("iftor") },
  { id: "memorial",   labelKey: "category.memorial",   count: _categoryCount("memorial") },
];

// International (utopian-invites.com / any .com host) price. Every template is
// a flat 149,000 UZS on .uz; internationally we sell at a single fixed USD
// price that MUST match the Lemon Squeezy product price (LS is what actually
// charges the card). Not a live FX conversion — a deliberate price point.
// If the LS product price changes, change this too.
const INTL_PRICE_USD = 14.99;

export const formatPrice = (n: number): string => {
  if (isIntlSite()) {
    return "$" + INTL_PRICE_USD.toFixed(2);
  }
  return new Intl.NumberFormat("uz-UZ").format(n) + " so'm";
};
